import {
  getSurveyZipcodeFromHomeAddress,
  hasBillingActiveSubscription,
} from "src/utils/billingData";
import {
  loadActiveSubscription,
  loadBillingUserHomeAddress,
} from "src/utils/redux/slices/billingData";
import {
  isRequestFailed,
  isRequestSuccess,
} from "src/utils/redux/requestState";
import zipState from "zip-state";
import { MedicareAttestationCheckoutPathEnumProto_MedicareAttestationCheckoutPath } from "@noom/noom-contracts/noom_contracts/events/buyflow/buyflow";

import {
  MHMDA_STATES,
  NOOM_MED_ADDON_SUBSCRIPTION_PRODUCT_NAME,
  routeConstants,
  SessionStorageKey,
} from "@utils/constants";
import { FEATURE_PARAM } from "@utils/medFeatureParams";
import { StatesLabsNotIncluded } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import {
  belowMinimumNormalBMI,
  calculateBMI,
  getCourseDuration,
  MINIMUM_NORMAL_BMI_MED,
} from "@utils/calculateGoals";
import mapPlanKeyToNoomPlanId, {
  clinicalPlans,
  planDurationMonthsToTelehealthNoomPlanId,
} from "@utils/plansMapping";
import {
  DE_COURSE_PACK_ELIGIBLE_COUNTRIES,
  EN_COURSE_PACK_ELIGIBLE_COUNTRIES,
} from "@utils/userSegment/constants";
import { queryClient } from "@utils/fetch";
import { genderConstants, sexConstants } from "@utils/sexAndGender";
import {
  CountryCode,
  getAppCountryCode,
  getCountryCode,
  getMeristemContext,
  getSubdivision,
} from "@utils/meristemContext";

import { appConfig } from "src/config";
import {
  getActiveShopOrderEntry,
  getSubscribedShopEntry,
  isShopProductInCart,
  isShopSubscriptionEntry,
  ShopProduct,
} from "src/utils/shopProducts";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  MEDICATION_PREFERENCE_ID,
  type MedicationPreferenceId,
} from "src/utils/medicationPreferenceIds";
import {
  FeatureFlags,
  getFeatureFlagValue,
} from "src/pageDefinitions/featureFlags";
import { getPageSet } from "src/pageDefinitions/pageSets";
import { TELEHEALTH_FREE_PLAN_ID } from "src/funnels/payment-en/pages/telehealthRedirects";
import {
  HEALTHY_WEIGHT_CURRICULUM_PREFIX,
  hrtPaymentCheckoutLayer,
  hrtPaymentResultsLayer,
  hrtSurveyLayer,
  noomMedOfferLayer,
  noomPremiumFitonSurveyLayer,
  noomPremiumSurveyLayer,
  noomSummitSurveyLayer,
  noomWeightMedBranchingLayer,
} from "src/pageDefinitions/goto/constants";
import {
  selectAMCoursepack,
  selectGlp1Coursepack,
  selectMovementCoursepack,
  selectMWCoursepack,
  selectSleepCoursepack,
} from "src/utils/redux/slices/paymentAddon";
import {
  canUseMobileSharingInterface,
  canUseNavigatorShare,
} from "src/hooks/sharingApi";
import { isHolidayPromotion } from "src/utils/holidayPromo";

import getStore, { CoreReduxState } from "../redux/store";
import {
  isEmailWinback,
  isFromEmail,
  isFromNoomWeightEmail,
  isUSEmail,
} from "./email";
import { checkSegments, createOneTimeConditionSegment } from "./util";
import { canRetrieveWeight } from "../refactoredSurvey";
import { loadWinbackInfo } from "../redux/slices/winbackInfo";
import {
  isAppAndroid,
  isAppAndroidWinback,
  isAppIosBase,
  isAppIosBaseAndWinback,
  isAppIOSUpsell,
  isAppVersionGTE,
  isFreemiumUnidentifiedRoute,
  isIOSPlatform,
} from "./in-app";
import { isMealPlan, isMealWorkoutBundle, isWorkoutPlan } from "../addons";
import { isInExperimentConflictingWithSummitCoursePack } from "../api/addons/addons";
import { getIOSMessageHandlers } from "../in-app/messageHandlers";
import {
  getPromoCodeState,
  isPromoCodePersisted,
} from "../redux/slices/promoCode";
import { loadActiveSubscriptionDataState } from "../redux/slices/currentSubscriptionData";
import { getMeristemConfigValue } from "../meristemConfig";
import { isMultiUserPlan } from "../multiUserPlan";
import {
  GET_PEERS_CACHE_KEY,
  loadPeers,
  MultiUserPlanPeerResponse,
} from "../api/noomTogether";
import { paymentAddonProducts } from "../paymentAddon";
import { isCheckoutOfferTimerExpired } from "../checkoutTimer";
import { isPersonalizedPlanTimerExpired } from "../personalizedPlanTimer";
import { getURLParams } from "../urlParams";
import { isDevModEnabled } from "../runtimeFlags";
import { hasFallbackPromotionalOfferConfiguration } from "../fallbackPromotionalOffer";
import { getCampaignFromStorage } from "../api/campaign";
import { freeMonthsOfferConfiguration } from "../freeMonthsOffer";
import { getSessionState } from "src/pageDefinitions/session";
import { calculateAge } from "../datetime";
import { ServerContextState } from "../redux/slices/serverContext";
import { isEmail } from "../validate";
import {
  IDEAL_WEIGHT_VIBE_OFFER_ID,
  shouldSuppressVibeOffer,
  STEP_TRACKING_VIBE_OFFER_ID,
} from "../noomVibe";
import {
  isAdWordsSEM,
  isAffiliate,
  isAffiliateWithoutTrial,
  isAffiliateWithTrial,
  isAppAndroidBase,
  isAppAU,
  isAppBaseRoute,
  isAppCA,
  isAppEmail,
  isAppFreeOnboardingProfileRoute,
  isAppFreeOnboardingRoute,
  isAppGB,
  isAppUS,
  isAU,
  isB2BUpsellClinicalRoute,
  isBaseline,
  isCA,
  isCalorieClinicalRoute,
  isClinicalRoute,
  isCMSWeightRoute,
  isCore,
  isCoreClinicalRoute,
  isCoreDirect,
  isCorePaid,
  isCorePaidWithoutAddWordsSEM,
  isCountry,
  isDeEmailMain,
  isDeLanguage,
  isDPP,
  isEmailMain,
  isEnLanguage,
  isEnMantle,
  isEnProgramLocale,
  isEsEmailMain,
  isEsLanguage,
  isEU,
  isFemaleSex,
  isFreemiumBranchedRoute,
  isFreemiumClinicalRoute,
  isFreemiumHealthRoute,
  isFreemiumIdentifiedRoute,
  isFreemiumQuizRoute,
  isFreemiumUpsellRoute,
  isFreemiumWeightRoute,
  isHealthRoute,
  isHM,
  isHRTRoute,
  isInApp,
  isInAppEmployerSurvey,
  isInAppWinback,
  isInsuranceCheckerRoute,
  isIntl,
  isIntlMantle,
  isLegacyAffiliateTrialRoute,
  isMaleGender,
  isMantle,
  isMobile,
  isNZ,
  isPreSignup,
  isReferralsBuyflow,
  isReferralsRoute,
  isStrictCore,
  isSubscriptionInvalid,
  isUK,
  isUpsellClinicalRoute,
  isUpsellRoute,
  isUS,
  isUSInAppWinback,
  isUSWinback,
  isWebClinicalRoute,
  isWebWinback,
  isWinback,
} from ".";
import {
  isBrandedAsyncNoomPlanId,
  isClinicalTierNoomPlanId,
  isCompoundedTirzepatideMedTier,
  isEligibleFor21DayTrial,
  isEligibleFor30DayTrial,
  isGlp1WeightLossMedProductName,
  isHRTNoomPlanId,
  isMedProductName,
  isMedTierPlanId,
  isPremiumTierPlanId,
  isWeightOrPremiumProductName,
} from "@utils/plans";
import {
  hasAnsweredPlanInterestInCurrentSession,
  hasAnsweredToGLP1FollowUpInCurrentSession,
  hasAnsweredToGLP1InCurrentSession,
} from "../glp1";
import { loadMedEntitlements } from "../redux/slices/medEntitlements";
import { isProductEligible } from "../redux/slices/productEligibility";
import {
  ADDON_SKU,
  isStateEligibleForAddon,
  PEPTIDE_SERMORELIN_PRODUCT_NAME,
  RX_NAD_PLUS_PRODUCT_NAME,
} from "../addonEligibility";
import {
  getLoadedSubscriptionStatus,
  loadSubscriptionStatus,
} from "src/hooks/subscriptionStatus";
import {
  BRANDED_MED_SKUS,
  BRANDED_VARIANT,
  ELIGIBLE_SKUS_FOR_SWITCH,
  getDefaultEligibilityOptions,
  getEligibleBrandedVariants,
  getEligibleGlp1Products,
  getEligibleMedProducts,
  getMedicalHealthRisks,
  isFulldoseGlp1Eligible,
  MED_DRIP_PROMO_ELIGIBLE_PLAN_SKUS,
  MED_FLOW,
  MED_SKU,
  MED_WEIGHT_LOSS_PLAN_SKUS,
  NON_MED_PLAN,
} from "../medEligibility";
import { getInAppAutoCookie, isUserAuthenticated } from "../authCookieParser";
import visitTracker from "@noom/visit-tracker";
import {
  getTopSymptomsBasedOnUserSelected,
  MenopauseSymptoms,
} from "@components/hrt/utils/menopauseSymptoms";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "@utils/noomSessionStorage";
import { TelehealthIntakeTheme } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/TelehealthIntakeTheme";
import { PageSet } from "src/pageDefinitions/types";
import {
  getLongevityMicrodoseRollout,
  getMicrodoseTirzepatideRollout,
  getTirzepatideRollout,
} from "../rollout";
import { isGlobalExitEnabled } from "../globalExit";
import { getLandingPageBMIs } from "src/utils/landingPageBMIs";
import { transformAgeYearsToAgeRange } from "@components/building/usePeopleHelpedValues";
import {
  isInsidePartnerWebView,
  isInsideWebView,
} from "src/pageDefinitions/goto/utils";
import { HealthReportUpsellContent, UpsellAdLocation } from "gql/graphql";
import { getActiveUser } from "@noom/hive-auth";
import * as mealLoggingInterstitialConfig from "../mealLoggingInterstitial/config";
import { getTrialDurationFromUserData } from "../trialDuration";
import { isPostAuthCheckoutSession } from "src/pages/p/login/post-auth/postAuthNavigation";
import type { PdFatigueAnswer } from "src/pages/payment/survey/pdFatigue";

import {
  isDisqualifiedFromMedicareBridgeByCondition,
  isEligibleForMedicareBridge,
} from "../medicareEligibility";
import {
  MED_MEMBERSHIP_FACADE,
  resolveMedMembershipFacade,
} from "../medMembership";
import { getFacadeFromFeatureParam } from "../medMembershipOrder";
import {
  COMPOUNDED_MEDICATION_PRODUCT_NAMES,
  DOSAGE_REQUIRED_PRODUCT_NAMES,
  MedProductName,
  NON_SELLABLE_PRODUCT_NAMES,
} from "@utils/medProductNames";
import { shouldShowNovoCheckout } from "src/checkout/components/pricing/price-breakdown/plans/shouldShowNovoCheckout";
import { isMedMembershipSku } from "../medSku";

export {
  isEligibleForMedicareBridge,
  isDisqualifiedFromMedicareBridgeByCondition,
};

const HW_NON_VIP_BATCH_ID_DEV = 1;
const HW_NON_VIP_BATCH_ID_PROD = 354;
export const DPP_OVERRIDE_PLAN = "dpp-12-month";
export const USD_MONTHLY_PLAN_ID = "option_d_20221201_Ex1084var1email";
export const USD_MONTHLY_PLAN_ID_7_DAY_TRIAL = "option_d_20221222_Ex1084var1";
export const BANDIT_LOCAL_STORAGE_KEY = "mab_key";

export function isDevMode() {
  return isDevModEnabled();
}

export function isPrerender() {
  // eslint-disable-next-line no-underscore-dangle
  return !!(window as any).__prerender;
}

export function evalResultWithFeatureFlag(
  result: boolean,
  flag: keyof FeatureFlags
) {
  const flagValue = getFeatureFlagValue(flag) as boolean;
  if (flagValue === undefined) {
    return result;
  }
  return flagValue;
}

export const shouldEnrollUser = (state?: CoreReduxState) => {
  return !isInApp(state) && !isAppEmail(state) && !isUpsellRoute(state);
};

export const isHMEligible = () => isUS() && isEnLanguage();

export function shouldSuppressIdealWeightVibeOffer() {
  return shouldSuppressVibeOffer(IDEAL_WEIGHT_VIBE_OFFER_ID);
}

export const isIdealWeightVibeOfferEligible = () =>
  isEnLanguage() && !isFreemiumUpsellRoute();

export const shouldShowIdealWeightVibeOffer = () =>
  isIdealWeightVibeOfferEligible() &&
  !shouldSuppressIdealWeightVibeOffer() &&
  !isFreemiumUnidentifiedRoute() &&
  !isHealthRoute();

const hadPreviousITunesSubscription = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // This parameter is set by the growth backend in the internal app endpoint
  // More details about that here: https://github.com/noom/eng-handbook/blob/master/teams/growth/product/Winbacks.md#more-about-in-app-routes
  return urlParams?.get("had_itunes_sub") === "true";
};

const disableLPSurveySegment = checkSegments({
  featureFlagOverride: "disableLPSurvey",
});

export const isSurveyOnLPEligible = checkSegments({
  oneOf: [
    routeConstants.exsf01d,
    routeConstants.exsf01,
    routeConstants.affiliateTrialNPB,
    routeConstants.appEmail,
  ],
  noneOf: [isHM, isInApp, disableLPSurveySegment],
});

export const isBraintreeStripeiOSEligible = checkSegments({
  allOf: [hadPreviousITunesSubscription, routeConstants.app14DayReturning],
});

const ADDONS_ORDERS_API_PURCHASE_CONTEXT_PREFIXES = [
  "offers-shop-",
  "shop-",
] as const;

export function isAddonsOrdersApiPurchaseContext() {
  const contextType = getMeristemContext().context_type;
  return ADDONS_ORDERS_API_PURCHASE_CONTEXT_PREFIXES.some((prefix) =>
    contextType?.startsWith(prefix)
  );
}

export const isInAppNativePurchaseEligible = (
  state: CoreReduxState = getStore().getState()
) => {
  const iosHandlers = getIOSMessageHandlers();
  const hasITunesMessageHandler =
    !!iosHandlers?.buyflowPurchaseProduct || isDevMode();

  if (!hasITunesMessageHandler) {
    return false;
  }

  return (
    isIOSPlatform() &&
    !acceptedNoomClinicalOffer(state) &&
    !isAddonsOrdersApiPurchaseContext() &&
    !isBraintreeStripeiOSEligible(state)
  );
};

export function linkoutFeatureFlagIsEnabled() {
  const meristemConfigValue = getMeristemConfigValue(
    "APPLE_PURCHASE_LINKOUT_ENABLED"
  );
  if (meristemConfigValue) {
    return meristemConfigValue === "true";
  }
  return appConfig.APPLE_PURCHASE_LINKOUT_ENABLED;
}

export const isApplePurchaseLinkoutEligible = (
  state: CoreReduxState = getStore().getState()
) => {
  const idm = isDevMode();
  return (
    isUS() &&
    isEnLanguage() &&
    linkoutFeatureFlagIsEnabled() &&
    (isFreemiumWeightRoute(state) || isFreemiumBranchedRoute(state)) &&
    isInAppNativePurchaseEligible(state) &&
    isIOSPlatform() &&
    (isAppVersionGTE("13.47.0") || idm) &&
    (!!getIOSMessageHandlers()?.openExternalBrowser || idm)
  );
};

const ffwdEligibleCurrencies = ["USD", "EUR", "GBP", "CAD", "AUD"];
export const isCurrencyFastForwardEligible = () => {
  const { currency = "USD" } = getStore().getState().serverContext;
  return ffwdEligibleCurrencies.includes(currency);
};

const isUSOnlyFastForwardEligibleRoute = checkSegments({
  allOf: [isUS],
  oneOf: [isEmailMain, isEmailWinback, isReferralsRoute],
});

export const hasPromoCodeIntl = checkSegments({
  allOf: [hasPromoCode, isIntl],
});

const isTrialDuration30 = () => getTrialDurationFromUserData() === 30;

export const isFastForwardEligible = checkSegments({
  allOf: [isEnLanguage, isUS],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical, isTrialDuration30],
  oneOf: [
    isCore,
    routeConstants.winback,
    isAppAndroidBase,
    isAffiliateWithTrial,
    isUSOnlyFastForwardEligibleRoute,
  ],
});

isFastForwardEligible.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

const isNoomPremiumEligibleUS = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isMantle,
    isHealthRoute,
    isWebWinback,
    routeConstants.emailMain,
    routeConstants.app14DayFtWeb,
  ],
});

export const isEligibleForLongTrial = () => {
  return isEligibleFor30DayTrial() || isEligibleFor21DayTrial();
};

export const isPremiumTextCoachingSubscriptionEligible = checkSegments({
  allOf: [isUS, isEnProgramLocale, isEnLanguage],
  noneOf: [userHasPurchasedNoomClinical],
});

export const isNoomPremiumSubscriptionEligible = checkSegments({
  allOf: [isEnLanguage],
});

export const isFreeSupporterPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isCore, isWebWinback, isEmailMain],
});

const isLanguageNoomPremiumEligible = checkSegments({
  oneOf: [isEnLanguage, isDeLanguage, isEsLanguage],
});

const isNoomPremiumEligibleIntl = checkSegments({
  allOf: [isIntl, isLanguageNoomPremiumEligible],
  oneOf: [
    isMantle,
    isWebWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
});

export const isInNoomPremiumSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === noomPremiumSurveyLayer ||
      getPageSet(linearBuyflow.pageSetName)?.layer ===
        noomPremiumFitonSurveyLayer,
  ],
});

export const isAddonStoreEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isAppIosBase, isTelehealthPlan, userHasPurchasedNoomClinical],
  oneOf: [
    routeConstants.direct,
    routeConstants.exsf01d,
    routeConstants.exsf01,
    isAffiliateWithoutTrial,
    routeConstants.emailMain,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    isWebWinback,
    isAppAndroidBase,
    routeConstants.affiliateTrialNPB,
    routeConstants.appEmail,
    routeConstants.app14DayFtWeb,
  ],
});

export const isBodyScanOnWeb = checkSegments({
  allOf: [isAppEmail],
});

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [isMobile, isMantle, isFromEmail, isWinback, isInAppWinback],
});

export const userHasPurchasedNoomPremium = checkSegments({
  allOf: [(state) => state.addOns.purchasedNoomPremium],
});

export const userHasPurchasedNoomPremiumWithTextCoaching = checkSegments({
  allOf: [(state) => state.addOns.purchasedNoomPremiumWithTextCoaching],
});

export const userHasPurchasedNoomPremiumSubscription = checkSegments({
  allOf: [(state) => state.addOns.purchasedNoomPremiumSubscription],
});

export const isNoomPremiumConsultationSurveyEligible = checkSegments({
  oneOf: [userHasPurchasedNoomPremium, hasBoughtMealAndWorkoutPlan],
});

export const navigateToNoomPremiumConsultationSurvey = checkSegments({
  allOf: [userHasPurchasedNoomPremium],
  oneOf: [
    isMantle,
    routeConstants.affiliateTrialNPB,
    isReferralsBuyflow,
    isEmailWinback,
    isWinback,
    isAppAndroidBase,
  ],
  noneOf: [userHasPurchasedNoomPremiumSubscription],
});

export const shouldNavigateToNoomPremiumFitonConsultationSurvey = checkSegments(
  {
    allOf: [
      userHasPurchasedNoomPremium,
      userHasPurchasedNoomPremiumSubscription,
    ],
    oneOf: [
      isMantle,
      routeConstants.affiliateTrialNPB,
      isReferralsBuyflow,
      isEmailWinback,
      isWinback,
      isAppAndroidBase,
    ],
  }
);

export const isImportantDateEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isMantle],
});

export const isFivePercentEligible = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [
    isMantle,
    isWinback,
    isUSEmail,
    isDeEmailMain,
    isInAppWinback,
    isUSInAppWinback,
  ],
  allOf: [canRetrieveWeight],
});

export const isFinalGraphEligible = checkSegments({
  oneOf: [isMantle, isUSWinback, isUSEmail, isUSInAppWinback],
});

export const isShowingPaymentGraphExtraContent = checkSegments({
  oneOf: [isMantle, isUSWinback, isInAppWinback],
});

/**
 * Verifies if the current user should see the final update graph
 */
export const shouldShowFinalUpdateGraph = (state = getStore().getState()) => {
  const surveyAnswers = getSurveyAnswers(state);
  const { weight, idealWeight, weightKg, idealWeightKg } = surveyAnswers;
  const courseDuration = getCourseDuration(surveyAnswers);

  // EnUSBaseline + App14DayBase routes
  if (
    weight &&
    idealWeight &&
    weightKg &&
    idealWeightKg &&
    isFivePercentEligible() &&
    courseDuration <= 8
  ) {
    return true;
  }

  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

function shouldCondenseOrShowFinalUpdateGraph(courseDuration: number) {
  const isCourseDurationEligible =
    courseDuration >= 2 &&
    ((courseDuration <= 8 && isIntlMantle()) || courseDuration <= 6);
  return (
    canRetrieveWeight() && isFinalGraphEligible() && isCourseDurationEligible
  );
}

export const hasNoTrial = checkSegments({
  oneOf: [
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
    routeConstants.ndpp,
  ],
});

export const isDirectTraffic = checkSegments({
  oneOf: [
    routeConstants.direct,
    routeConstants.appUpsell,
    routeConstants.hmDirect,
    routeConstants.cmsWeight,
  ],
});

export const isTrialFeeWaived = checkSegments({
  oneOf: [
    isIOSPlatform,
    isInAppWinback,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    routeConstants.hmReferrals,
    routeConstants.affiliate,
    routeConstants.phillips,
    routeConstants.humana,
  ],
});

export const isChooseTrialPriceNewStyleEligible = checkSegments({
  oneOf: [isMantle, isEmailMain, isHM, isInApp],
});

export const isTrialEligible = (
  { recommendedPlan } = getStore().getState()
) => {
  return recommendedPlan.has_trial && !hasNoTrial();
};

export function showChooseTrialPage(state = getStore().getState()) {
  if (isInAppNativePurchaseEligible(state) || isOnAppleExternalLinkoutPath()) {
    return false;
  }
  if (isEnLanguage()) {
    return (
      !isTrialFeeWaived() &&
      !hasNoTrial() &&
      // Note (Vitza): As the content of this file is quite logically put
      // together by domains, it would need a considerable reorder to have this
      // method be defined above this line.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      !showMedCheckout() &&
      !hasMedicareInsurance()
    );
  }
  return (
    isCoreDirect(state) ||
    isCorePaid(state) ||
    isDeEmailMain() ||
    isEsEmailMain() ||
    isHM()
  );
}

export const isPromoCodeBoxEnabled = checkSegments({
  noneOf: [
    isInApp,
    isInAppNativePurchaseEligible,
    isOnAppleExternalLinkoutPath,
    isCorePaidWithoutAddWordsSEM,
    isWinback,
    routeConstants.affiliateNoTrial,
  ],
  featureFlagOverride: "togglePromoCode",
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [isInApp],
});

export const isPaidTraffic = checkSegments({
  oneOf: [
    isCorePaid,
    routeConstants.experiment,
    routeConstants.affiliateTrialNPB,
    routeConstants.hmPaid,
    routeConstants.hmExsf01,
    routeConstants.hmExsf01d,
    routeConstants.hmAwsem,
    routeConstants.winback,
    routeConstants.hmWinback,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [isMantle, isWinback],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [isMantle, isWinback, routeConstants.hmPaid, routeConstants.hmDirect],
});

const searchActiveRenewingOrPendingAddonSubscription = (
  serverContext: ServerContextState,
  expectedProductType?: string
) => {
  return (serverContext?.addon_subscriptions || []).find(
    (addOnSubscription) => {
      return (
        addOnSubscription &&
        (!expectedProductType ||
          addOnSubscription.product === expectedProductType) &&
        ((addOnSubscription.status === "ACTIVE" &&
          addOnSubscription.auto_renewal_enabled) ||
          addOnSubscription.status === "PENDING")
      );
    }
  );
};

export const hasActiveMedAddOnSubscription = () => {
  return searchActiveRenewingOrPendingAddonSubscription(
    getStore().getState().serverContext,
    NOOM_MED_ADDON_SUBSCRIPTION_PRODUCT_NAME
  );
};

export const hasActiveAddOnSubscription = (
  serverContext: ServerContextState
) => {
  return searchActiveRenewingOrPendingAddonSubscription(serverContext);
};

export const shouldShowCounterOfferAddonDisclaimerInNoomWeightCounterOffer = (
  serverContext: ServerContextState
) => {
  return isEnLanguage() && isUS() && hasActiveAddOnSubscription(serverContext);
};

export const isEligibleForNX1CourseEnhancements = checkSegments({
  allOf: [isEnLanguage, shouldShowCoursePackCheckoutOffers],
  noneOf: [isUK],
});

export const isEligibleForFreeAgingMetabolismOffer = checkSegments({
  allOf: [isEligibleForNX1CourseEnhancements, isIntl],
});

export const isEligibleForFreemiumWeightBuyflow = checkSegments({
  oneOf: [isFreemiumWeightRoute, isFreemiumBranchedRoute],
});

export const isBMINoomClinicalEligible = () => {
  const isFreemiumHealth = isFreemiumHealthRoute();
  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: false,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: !isFreemiumHealth,
      requireHighestBMIAnswer: !isFreemiumHealth,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isBMINoomClinicalEligibleBeforePrevMeds = () => {
  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: false,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isCurrentHealthRiskNoomClinicalEligible = () => {
  return (
    getEligibleMedProducts({
      checkBMI: false,
      checkHealthRisks: true,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

export const isEligibleForMedAfterHealthRiskQuestion = () => {
  // For FreemiumHealthRoute, these questions are skipped in the survey flow,
  // so we don't require them for eligibility checks
  const isFreemiumHealth = isFreemiumHealthRoute();

  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: true,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: !isFreemiumHealth,
      requireHighestBMIAnswer: !isFreemiumHealth,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
      skipEatingDisorderCheck: isHealthRoute(),
    }).length > 0
  );
};

export const isEligibleForMedAfterZipcodeQuestion = () => {
  // For FreemiumHealthRoute, these questions are skipped in the survey flow,
  // so we don't require them for eligibility checks
  const isFreemiumHealth = isFreemiumHealthRoute();

  return (
    getEligibleMedProducts({
      checkBMI: true,
      checkHealthRisks: true,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: !isFreemiumHealth,
      requireHighestBMIAnswer: !isFreemiumHealth,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    }).length > 0
  );
};

const getAgeFromDob = ({ surveyAnswers } = getStore().getState()) => {
  const { dateOfBirth } = surveyAnswers;
  return calculateAge(new Date(dateOfBirth));
};

export const isYoungerThan18 = (state = getStore().getState()) => {
  return getAgeFromDob(state) < 18;
};

export const isAgeEligibleForMed = (age: number) => {
  return age >= 18 && age < 80;
};

export const isAgeFromDobEligibleForMed = (state = getStore().getState()) => {
  return isAgeEligibleForMed(getAgeFromDob(state));
};

// Actual Medicare eligibility age (65), unlike mainSurvey's Bridge screening
// gate which uses the broader "60s"/"70s" ageRange bucket (60-79).
export const isAgeEligibleForMedicareBridgeScreening = (age: number) => {
  return age >= 65 && age < 80;
};

export const isAgeFromDobEligibleForMedicareBridgeScreening = (
  state = getStore().getState()
) => {
  return isAgeEligibleForMedicareBridgeScreening(getAgeFromDob(state));
};

export const isAgeEligibleForMht = (age: number) => {
  return age >= 45 && age < 60;
};

export const willReceiveMenstrualCycleModule = (
  { surveyAnswers } = getStore().getState()
) => {
  const { menstrualCycle } = surveyAnswers;
  return ["yes", "preferNotToSay"].includes(menstrualCycle?.[0]);
};

export const isMedLead = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams?.get("utm_source");
  return utmSource === "MedDrip" || utmSource?.toLowerCase() === "ghost";
};

/**
 * Checks if user is coming from Healthline branded meds campaign
 * Requires both utm_source=healthline AND utm_campaign=brandedmed
 */
export function isHealthlineBrandedMedsTraffic(): boolean {
  const urlParams = getURLParams();
  return (
    urlParams?.utm_source === "healthline" &&
    urlParams?.utm_campaign === "brandedmed"
  );
}

export const noomClinicalOptedIn = (
  { surveyAnswers } = getStore().getState()
) => {
  const { medOptIn } = surveyAnswers;
  // Note (zhuowei): For med re-engagement leads (e.g. MedDrip, Ghost), always assume opt-in
  return medOptIn?.[0] === "yes" || isMedLead();
};

export const isZipcodeLabsExcluded = (
  { surveyAnswers } = getStore().getState()
) => {
  const { zipcode } = surveyAnswers;
  if (zipcode !== undefined) {
    const state = zipState(zipcode);
    return state in StatesLabsNotIncluded;
  }
  return false;
};

export const isMedicareStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return !surveyAnswers.medicare?.includes("yes");
};

export const isGlp1StatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return surveyAnswers.glp1?.includes("no");
};

export const isAgeRangeStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return !surveyAnswers.ageRange?.includes("80s+");
};

export const isPregnantStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return !surveyAnswers.pregnant?.includes("noButNursing");
};

export const isPregnantStatusHealthCheckEligible = (
  { surveyAnswers: { pregnant } } = getStore().getState()
) => {
  return !["noButNursing", "yes"].includes(pregnant?.[0]);
};

export const isNotPlanningToBePregnant = (
  { surveyAnswers: { pregnant } } = getStore().getState()
) => {
  return (pregnant?.[0] as string) !== "noButPlanning";
};

export const isEatingDisorderStatusNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  return surveyAnswers.eatingDisorder?.[0] !== "yes";
};

export const shouldAskMedicareQuestionPostPlanSelection = (
  state = getStore().getState()
) => {
  const selectedMedPlan = state.recommendationContext?.selectedMedPlan;
  if (!selectedMedPlan) return false;

  // Users who already told us they have Medicare don't need to be re-asked
  // post plan selection.
  if (hasMedicareInsurance()) {
    return false;
  }

  return (BRANDED_MED_SKUS as (typeof selectedMedPlan)[]).includes(
    selectedMedPlan
  );
};

export const isMedicareStatusNoomClinicalEligibleAtCheckout = (
  state = getStore().getState()
) => {
  // Novo payment results no longer asks the Medicare question, so checkout
  // cannot require that answer.
  if (isNovoBuyflowEnabled()) {
    return true;
  }

  const mustAnswerMedicare =
    shouldAskMedicareQuestionPostPlanSelection(state) &&
    shouldShowMedicareQuestionPostBrandedCashPay(state);

  if (mustAnswerMedicare) {
    return getSurveyAnswers(state).medicare?.[0] === "no";
  }

  return (
    !(
      hasSelectedBrandedPlan() ||
      hasSelectedZepboundVialPlan() ||
      hasSelectedWegovyPillPlan()
    ) || isMedicareStatusNoomClinicalEligible(state)
  );
};

// The cash-pay preference question is only meaningful when the user has a
// real choice, i.e. both the sync and async branded variants are eligible.
export const isBrandedCashPayQuestionEligible = () => {
  const eligibleVariants = getEligibleBrandedVariants();
  return (
    eligibleVariants.includes(BRANDED_VARIANT.SYNC) &&
    eligibleVariants.includes(BRANDED_VARIANT.ASYNC)
  );
};

export function shouldShowMedicareQuestionPostBrandedCashPay(
  state = getStore().getState()
) {
  const preference = getSurveyAnswers(state).brandedCashPay?.[0];
  return preference !== "cashPay";
}

export const shouldSeeGlp1Companion = (state = getStore().getState()) => {
  const { previousMedications, glp1FollowUp, glp1 } = getSurveyAnswers(state);
  const previousMedicationsAnswer =
    previousMedications?.includes("yes") ||
    previousMedications?.includes("takingWeightLossMed");
  const glp1FollowUpAnswer = glp1FollowUp?.includes("no");
  const glp1Answer = glp1?.includes("yes") || glp1?.includes("prescription");

  return (previousMedicationsAnswer && glp1FollowUpAnswer) || glp1Answer;
};

export const isEligibleForClinicalBuyflow = checkSegments({
  allOf: [isClinicalRoute, isUS, isEnLanguage],
});

export const showMedIneligiblePage = (state = getStore().getState()) => {
  const eligibleSaleItems = state.recommendationContext?.eligibleSaleItems;

  const { selectedMedPlanType } = getSessionState("browser");
  const selectedMedSku = getSelectedMedPlanType();

  const hasEligibleSaleItems = selectedMedPlanType
    ? !!eligibleSaleItems?.find((item) => item.medSku === selectedMedSku)
    : eligibleSaleItems?.length > 0;

  return (
    !isEligibleForClinicalBuyflow(state) ||
    (isB2BUpsellClinicalRoute(state) && !hasEligibleSaleItems)
  );
};

export function isStandaloneBuyflowHRTEnabled() {
  return (
    getMedFlagOverride("standaloneHRTBuyflowEnabledOverwrite") ??
    !!appConfig.STANDALONE_HRT_BUYFLOW_ENABLED
  );
}

export const isEligibleForStandaloneHRTBuyflow = checkSegments({
  allOf: [isHRTRoute, isUS, isEnLanguage, isStandaloneBuyflowHRTEnabled],
});

export function isHealthRouteEnabled() {
  return (
    getMedFlagOverride("healthRouteEnabledOverwrite") ??
    !!appConfig.HEALTH_ROUTE_ENABLED
  );
}

function prefersBrandedCashpay() {
  const surveyAnswers = getSurveyAnswers();
  return (
    surveyAnswers.brandedCashPay?.[0] === "cashPay" && isBrandedAsyncEnabled()
  );
}

export function shouldUseBrandedAsyncPlan() {
  // Novo sells the async branded plan only for the Medicare bridge. Every
  // other branded recommendation is the sync plan.
  if (isNovoBuyflowEnabled()) {
    return isEligibleForMedicareBridge();
  }
  if (!getEligibleBrandedVariants().includes(BRANDED_VARIANT.ASYNC)) {
    return false;
  }
  return hasMedicareInsurance() || prefersBrandedCashpay();
}

/**
 * True when the checkout should render in account-first order
 * (Personalized Plan → Account → Payment → Review).
 *
 * As of MGWL-875 (milestone 1) this is the permanent behavior on the
 * `clinical`, `freemium-upsell-clinical`, `freemium-upsell-health`, and
 * `health` routes. All other routes still see this only when the Telex289
 * experiment variant is on. Once Telex289 is fully baselined, drop the
 * `isTelex289Active()` clause.
 */
export const isProductSwitchCheckout = () => {
  return window.location.pathname.includes("/productSwitchCheckout");
};

export function isAccountAccordionFirstCheckoutOrder() {
  return (
    isCoreClinicalRoute() ||
    isFreemiumClinicalRoute() ||
    isFreemiumHealthRoute() ||
    isHealthRoute() ||
    isTelex289Active() ||
    isProductSwitchCheckout()
  );
}

export function shouldLoginAtCheckout() {
  if (getActiveUser()?.accessCode) {
    return false;
  }
  return isCoreClinicalRoute() || isHealthRoute();
}

export const isEligibleForHealthBuyflow = checkSegments({
  allOf: [isHealthRoute, isUS, isEnLanguage, isHealthRouteEnabled],
});

export function isInsuranceCheckerRouteEnabled() {
  return (
    getMedFlagOverride("insuranceCheckerRouteEnabledOverwrite") ??
    !!appConfig.INSURANCE_CHECKER_ROUTE_ENABLED
  );
}

export const isEligibleForInsuranceCheckerBuyflow = checkSegments({
  allOf: [
    isInsuranceCheckerRoute,
    isUS,
    isEnLanguage,
    isInsuranceCheckerRouteEnabled,
  ],
});

export const isInsuranceCheckerBMIEligible = () =>
  getEligibleMedProducts({
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
  }).length > 0;

export const isInsuranceCheckerStateEligible = () =>
  getEligibleMedProducts({
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    checkBMI: true,
    checkHealthRisks: false,
    checkState: true,
  }).length > 0 && isZipcodeCorrespondingToUSState();

export const isInsuranceCheckerHealthRiskEligible = () =>
  getEligibleMedProducts({
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    checkBMI: false,
    checkHealthRisks: true,
    checkState: false,
  }).length > 0;

export const isInsuranceCheckerHealthRiskAndStateEligible = () =>
  getEligibleMedProducts({
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    checkBMI: false,
    checkHealthRisks: true,
    checkState: true,
  }).length > 0;

export const isInsuranceCheckerMetforminOnlyEligible = () => {
  const insuranceCheckerEligibleProducts = getEligibleMedProducts();

  return (
    insuranceCheckerEligibleProducts.length === 1 &&
    insuranceCheckerEligibleProducts[0] === MED_SKU.ORALS_METFORMIN
  );
};

export function isCompounded503aTransitionEnabledWithOverride() {
  return (
    getMedFlagOverride("compounded503aTransitionEnabledOverwrite") ??
    !!appConfig.MED_COMPOUNDED_503B_TO_503A_PHARMACY_TRANSITION
  );
}

export const isCompounded503aTransitionEnabled = checkSegments({
  allOf: [isCompounded503aTransitionEnabledWithOverride],
});

export function isAddressChangeEnabledWithOverride() {
  return (
    getMedFlagOverride("addressChangeEnabledOverwrite") ??
    !!appConfig.MED_IS_ADDRESS_CHANGE_ENABLED
  );
}

export const isAddressChangeEnabled = checkSegments({
  allOf: [isAddressChangeEnabledWithOverride],
});

export function isTreatmentsInitializedGateEnabledWithOverride() {
  return (
    getMedFlagOverride("treatmentsInitializedGateEnabledOverwrite") ??
    !!appConfig.TREATMENTS_INITIALIZED_GATE_ENABLED
  );
}

/**
 * Selects which readiness check gates the clinical intake.
 *
 * On: `areTreatmentsInitialized`, scoped to the sale items the user actually
 * bought, which is the only check that works once a session can produce more
 * than one med purchase.
 * Off: the legacy `isPatientInitialized` poll.
 *
 * Kept as a toggle rather than a straight cutover because this ships before
 * Sermorelin launches, and the new check drops the vendor-readiness half of
 * `isPatientInitialized` (MDI voucher / Beluga encounters).
 */
export const isTreatmentsInitializedGateEnabled = checkSegments({
  allOf: [isTreatmentsInitializedGateEnabledWithOverride],
});

export const isUserNoomClinicalEligibleAtCheckout = checkSegments({
  allOf: [
    isEligibleForClinicalBuyflow,
    isBMINoomClinicalEligible,
    isMedicareStatusNoomClinicalEligibleAtCheckout,
    isCurrentHealthRiskNoomClinicalEligible,
  ],
});

export const isUserEligibleForHealthBuyflowAtCheckout = (
  state = getStore().getState()
) => {
  const { recommendationContext } = state;
  if (
    !isEligibleForHealthBuyflow() ||
    recommendationContext?.recommendedHealthPlan === NON_MED_PLAN.FREEMIUM
  ) {
    return false;
  }
  if (hasSelectedNoomWeightPlan()) {
    return true;
  }
  return isEligibleForMedProducts();
};

export const shouldShowNoomClinicalCheckoutOffer = (
  state = getStore().getState()
): boolean => {
  const { promoCode } = state;
  return (
    isUserNoomClinicalEligibleAtCheckout(state) &&
    !promoCode.promoCodeIsVip &&
    noomClinicalOptedIn(state)
  );
};

export const isInAppMedUpgrade = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow?.pageSetName)?.layer === noomMedOfferLayer,
  ],
});

export function isBrandedAsyncEnabled() {
  return isClinicalRoute() || isInAppMedUpgrade();
}

export const isDisabledNWSwitchForMed = () => {
  return (
    isInAppMedUpgrade() ||
    isUpsellRoute() ||
    isCalorieClinicalRoute() ||
    getFeatureFlagValue("disableNWSwitchForMed")
  );
};

export const isUserSegmentEligibleForMedSwitchBuyflow = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isReferralsRoute,
    isAppBaseRoute,
    isInAppWinback,
    isAffiliate,
    isFreemiumBranchedRoute,
  ],
});

export const isEligibleForMedSwitchBuyflow = checkSegments({
  allOf: [isUserSegmentEligibleForMedSwitchBuyflow, isBMINoomClinicalEligible],
  noneOf: [hasSignedLiabilityWaiver],
});

export const isNoomWeightMedBranching = checkSegments({
  allOf: [
    ({ linearBuyflow }) => {
      const layer = getPageSet(linearBuyflow.pageSetName)?.layer;
      return layer === noomWeightMedBranchingLayer;
    },
  ],
});

export const isWeightToMedRedirect = () => {
  const { isWeightToMedSwitch } = getSessionState("browser");
  return Boolean(isWeightToMedSwitch);
};

export const disableAccountFieldsForMedFlow = () => {
  const { disableAccountFieldsForMed } = getSessionState("browser");
  return Boolean(disableAccountFieldsForMed) && isClinicalRoute();
};

export const isPregnantStatusEligibleForHRT = () => {
  const { pregnant } = getSurveyAnswers();
  return pregnant?.includes("no");
};

export const isUserNoomHRTEligibleAtCheckout = checkSegments({
  allOf: [
    isEligibleForStandaloneHRTBuyflow,
    isAgeRangeEligibleForHRT,
    isLastPeriodEligibleForHRT,
    isPeriodEndReasonEligibleForHRT,
    isMenopauseSymptomsEligibleForHRT,
    isPregnantStatusEligibleForHRT,
    isEligibleForHrtAfterZipcodeQuestion,
    isEligibleForHrtAfterHealthRiskQuestion,
  ],
});

const isMedCheckoutEligible = checkSegments({
  oneOf: [
    shouldShowNoomClinicalCheckoutOffer,
    isUserNoomHRTEligibleAtCheckout,
    isUserEligibleForHealthBuyflowAtCheckout,
  ],
});

/**
 * Keeps the med checkout layout during payment when the cart already holds a
 * clinical/HRT plan and the user accepted the clinical offer. Prevents live
 * survey re-checks (e.g. Medicare after brandedCashPay changes) from swapping
 * to HW pricing mid-session. Scoped to an active med cart so other flows are
 * unchanged.
 */
function isActiveMedCheckoutSession(state = getStore().getState()) {
  const { checkout, checkoutOrders, recommendedPlan } = state;
  if (!checkout?.acceptedClinicalOffer) {
    return false;
  }
  const planId =
    checkoutOrders?.saleItem?.legacyNoomPlanId ?? recommendedPlan?.noom_plan_id;
  const productName =
    checkoutOrders?.recurringBillingCycle?.invoice?.lineItems?.[0]?.productName;
  return (
    isClinicalTierNoomPlanId(planId) ||
    isHRTNoomPlanId(planId) ||
    isMedProductName(productName)
  );
}

export function showMedCheckout(state = getStore().getState()): boolean {
  if (isActiveMedCheckoutSession(state)) {
    return true;
  }
  return isMedCheckoutEligible(state);
}

if (isMedCheckoutEligible.load) {
  showMedCheckout.load = isMedCheckoutEligible.load;
}

export function isTelehealthPlan(state = getStore().getState()): boolean {
  const noomPlanId = state?.recommendedPlan?.noom_plan_id;
  const paidTelehealthPlans = Object.values(
    planDurationMonthsToTelehealthNoomPlanId
  );
  return (
    paidTelehealthPlans?.includes(noomPlanId) ||
    noomPlanId === TELEHEALTH_FREE_PLAN_ID
  );
}

export function isRecommendedPlanMed(state = getStore().getState()): boolean {
  const noomPlanId = state?.recommendedPlan?.braintree_id;
  return isMedTierPlanId(noomPlanId);
}

export function userHasPurchasedNoomClinical(state = getStore().getState()) {
  const { purchasedNoomClinical, userData } = state;

  // TODO: Move to using a more reliable way like an API instead of using
  // this userdata flag to determine who purchased Noom Med
  return purchasedNoomClinical || userData.purchasedNoomClinical;
}

const TELEHEALTH_CONTEXT_TYPES = ["telehealth", "telehealth-followup"];

export function isTelehealth() {
  return TELEHEALTH_CONTEXT_TYPES.includes(getMeristemContext().context_type);
}

/**
 * Signals if the current user/experience is subject to HIPPA data protection rules.
 * This can be used to filter logging/etc as appropriate to ensure compliance.
 */
export const isHipaa = (): boolean => {
  return isTelehealth();
};

export const isHmSuperswitchEligible = (): boolean => {
  const { currentSubscriptionData } = getStore().getState();
  return (
    isEnLanguage() &&
    currentSubscriptionData.hasActiveSubscription &&
    currentSubscriptionData.activeSubscriptionCurriculumSlug &&
    currentSubscriptionData.activeSubscriptionCurriculumSlug.endsWith(
      "en_US"
    ) &&
    !isMultiUserPlan(currentSubscriptionData?.planId) &&
    ((currentSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_PROD &&
      appConfig.NOOM_ENV === "production") ||
      currentSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_DEV)
  );
};

isHmSuperswitchEligible.load = () => {
  const store = getStore();
  const { email } = store.getState().userData;
  if (email) {
    return getStore()
      .dispatch(loadActiveSubscriptionDataState({ email }))
      .then(() => {});
  }
  return Promise.resolve();
};

export const isGlp1OrMed = (): boolean => {
  const { medEntitlements } = getStore().getState();
  return (
    medEntitlements.hasTelehealthAddOn ||
    medEntitlements.hasGlp1CoursePack ||
    medEntitlements.hasMedCompanion
  );
};

isGlp1OrMed.load = () => {
  return getStore()
    .dispatch(loadMedEntitlements())
    .then(() => {});
};

export const userHasAccessCode = (): boolean => {
  return !!getStore().getState().userData.access_code;
};

export const isInHouseSharePageEligible = checkSegments({
  allOf: [userHasAccessCode],
});

export const isEligibleForNoomTogether = checkSegments({
  allOf: [isEnLanguage, userHasAccessCode],
});

isEligibleForNoomTogether.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

export const hasFamilyPlanEligibleSubscription = () => {
  const { isIAPsubscription, hasActiveSubscription, planId } =
    getStore().getState()?.currentSubscriptionData;
  const peers =
    queryClient.getQueryData<MultiUserPlanPeerResponse[]>(
      GET_PEERS_CACHE_KEY
    ) || [];

  return (
    hasActiveSubscription &&
    !isIAPsubscription &&
    !isPremiumTierPlanId(planId) &&
    !isMultiUserPlan(planId) &&
    !userHasPurchasedNoomClinical() &&
    peers.length === 0
  );
};

hasFamilyPlanEligibleSubscription.load = () => {
  return Promise.all([
    getStore().dispatch(loadActiveSubscriptionDataState({})),
    loadPeers(),
  ]);
};

const isNotEnrolledInExR143 = (state: CoreReduxState) => {
  const { userData } = state;
  return !userData?.usedBaselinePriceForMultiUserPlan;
};

export const isFamilyPlanSurveyEligible = checkSegments({
  allOf: [
    isUS,
    isEnLanguage,
    hasFamilyPlanEligibleSubscription,
    isNotEnrolledInExR143,
  ],
});

export const isEligibleForExitIntentModalEmail = () => {
  return (
    isIntl() &&
    isEmailMain() &&
    visitTracker.getTrackingParams().utm_source === "drip"
  );
};

export function hasPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);
  return !!promoCode?.promoCodeApplied;
}

export function hasVipPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);

  return hasPromoCode() && promoCode?.promoCodeIsVip;
}

export const isTaxExclusiveCountry = () => isCountry(["US", "CA"]);

export const isNoomPremiumConsultationSurveyDisqualified = (
  state = getStore().getState()
) => {
  const { surveyAnswers } = state;
  return (
    surveyAnswers?.workoutPlanLimitations?.includes("painOrLosingBreath") ||
    surveyAnswers?.fitonWorkoutPlanLimitations?.includes("painOrLosingBreath")
  );
};

export const isNoomPremiumEligible = (state = getStore().getState()) => {
  const promoCode = getPromoCodeState();
  const isNoomPremiumEligibleBySegments =
    isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();
  const isCurrentPlanPremiumTier = state.serverContext?.is_on_premium_tier_plan;
  return (
    !isCurrentPlanPremiumTier &&
    !promoCode?.promoCodeIsVip &&
    isNoomPremiumEligibleBySegments
  );
};

export const requiresDoubleOptInForEmail = () => {
  return isCountry(["DE", "AT"]);
};

export const isWinbackUserAndNotReup = () => {
  const { winbackInfo } = getStore().getState();

  return (
    ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus) &&
    !winbackInfo.willReup
  );
};

isWinbackUserAndNotReup.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isWinbackUser = () => {
  const { winbackInfo } = getStore().getState();

  return ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus);
};

isWinbackUser.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isReUp = (state = getStore().getState()) => {
  const { winbackInfo } = state;
  return winbackInfo.willReup;
};

isReUp.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const showPromoBox = checkSegments({
  noneOf: [routeConstants.exsf01, routeConstants.exsf01d, isWinback],
});

export function determinePlanOverride() {
  if (isDPP()) {
    return DPP_OVERRIDE_PLAN;
  }
  return "";
}

export const isDECoursePackEligible = () => {
  return isCountry(DE_COURSE_PACK_ELIGIBLE_COUNTRIES) && isDeLanguage();
};

const isRouteCoursePackEligible = checkSegments({
  oneOf: [
    isCore,
    isBaseline,
    isReferralsBuyflow,
    isEmailMain,
    isEmailWinback,
    isAppAndroidBase,
    isWinback,
    isAffiliate,
    isLegacyAffiliateTrialRoute,
    isFreemiumWeightRoute,
    isFreemiumBranchedRoute,
  ],
});

export function mwCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.noomMoodBundle?.includes("yes");
}

export function amCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.agingMetabolismCoursepack?.includes("yes");
}

export function movementCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.movementCoursepack?.includes("yes");
}

export function sleepCoursepackOptedIn(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.sleepCoursepack?.includes("yes");
}

export function glp1CoursepackOptedIn() {
  return (
    hasAnsweredToGLP1InCurrentSession("yes") ||
    hasAnsweredToGLP1InCurrentSession("prescription") ||
    hasAnsweredToGLP1FollowUpInCurrentSession("no")
  );
}

const isENCoursePackEligible = () => {
  return isCountry(EN_COURSE_PACK_ELIGIBLE_COUNTRIES) && isEnLanguage();
};

const isLocaleCoursePackEligible = () => {
  return isENCoursePackEligible() || isDECoursePackEligible();
};

export function shouldShowCoursePackSurveys(state = getStore().getState()) {
  const { recommendedPlan } = state;
  return (
    isLocaleCoursePackEligible() &&
    isRouteCoursePackEligible() &&
    recommendedPlan.has_trial &&
    !isReUp(state) &&
    !showMedCheckout() &&
    // Course packs are not supported for native purchases
    !isInAppNativePurchaseEligible(state) &&
    !isOnAppleExternalLinkoutPath()
    // Originally we only showed course pack surveys if
    // you were not eligible for Noom Med. However we will support both
    // Moom Med and Mental Wellness, and selectively suppress
    // AM for now while 3 course packs support is built out.
  );
}

shouldShowCoursePackSurveys.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isRouteEligibleForGLP1CEOfferOnly = checkSegments({
  oneOf: [isClinicalRoute, isAppAndroidWinback],
});

export const isEligibleForGLP1CEOfferOnly = checkSegments({
  allOf: [isRouteEligibleForGLP1CEOfferOnly, shouldSeeGlp1Companion],
});

export function shouldShowCoursePackCheckoutOffers(
  state = getStore().getState()
) {
  const { promoCode } = state;
  return (
    (shouldShowCoursePackSurveys() || isEligibleForGLP1CEOfferOnly()) &&
    !promoCode.promoCodeIsVip &&
    !showMedCheckout()
  );
}

export const shouldShowPaymentAddonCheckoutOffers = checkSegments({
  oneOf: [shouldShowCoursePackCheckoutOffers],
});

export const shouldPreselectMWCoursepackCheckoutOffer = checkSegments({
  oneOf: [
    // Via buyflow coursepack surveys
    (state) =>
      shouldShowCoursePackCheckoutOffers(state) && mwCoursepackOptedIn(state),
    // Via email route modal
    ({ userData }) =>
      userData?.answerToFreeMWCPModal === "yes" &&
      userData?.checkoutDiscountedMWCPTimerExpirationTimestamp &&
      userData.checkoutDiscountedMWCPTimerExpirationTimestamp > Date.now(),
  ],
  noneOf: [isReUp],
});

export const shouldPreselectAMCoursepackCheckoutOffer = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers, amCoursepackOptedIn],
});

export const shouldPreselectSleepCoursepackCheckoutOffer = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers, sleepCoursepackOptedIn],
});

export const shouldPreselectMovementCoursepackCheckoutOffer = checkSegments({
  allOf: [shouldShowCoursePackCheckoutOffers, movementCoursepackOptedIn],
});

export function acceptedMWCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(
      paymentAddonProducts.mentalWellness
    ) &&
    Boolean(selectMWCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedAMCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(
      paymentAddonProducts.agingMetabolism
    ) &&
    Boolean(selectAMCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedNoomClinicalOffer(state = getStore().getState()) {
  const { checkout } = state;
  return (
    isInAppMedUpgrade() ||
    (showMedCheckout(state) && checkout.acceptedClinicalOffer)
  );
}

export function getMedicareAttestationCheckoutPath():
  | MedicareAttestationCheckoutPathEnumProto_MedicareAttestationCheckoutPath
  | undefined {
  const planType = getSelectedMedPlanType();
  const isOnMembershipCheckoutPlan = shouldShowNovoCheckout({
    isNovoBuyflowEnabled: isNovoBuyflowEnabled(),
    isMembershipPlan: isMedMembershipSku(planType),
    isBrandedPlan: planType === MED_SKU.BRANDED,
  });

  const isOnLegacyBrandedPlan = BRANDED_MED_SKUS.includes(planType);
  if (!isOnLegacyBrandedPlan && !isOnMembershipCheckoutPlan) {
    return undefined;
  }
  // The membership plan is always cash pay, even for a bridge-eligible member
  // who chose it from the other options.
  if (isMedMembershipSku(planType)) {
    return "CASH_PAY";
  }
  return isEligibleForMedicareBridge() ? "MEDICARE_PAY" : "CASH_PAY";
}

export function acceptedSleepCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(paymentAddonProducts.sleep) &&
    Boolean(selectSleepCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedMovementCoursepackOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(paymentAddonProducts.movement) &&
    Boolean(selectMovementCoursepack(paymentAddon)) &&
    shouldShowCoursePackCheckoutOffers(state)
  );
}

export function acceptedGlp1CompanionOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.paymentAddonsInCart.includes(paymentAddonProducts.glp1Companion) &&
    Boolean(selectGlp1Coursepack(paymentAddon))
  );
}

export function isEligibleForPersonalizedPlanTimer() {
  // Note(Pratik) inAppIos is the only route where they saw the original timer
  // but are not eligible for coursepacks to see the new timer due to itunes addon purchase
  // limitations so we will preserve their experience
  // Note (kevinh) DE coursepack users are not eligible for free plans so they will also
  // see the original timer function
  return (
    !isInAppEmployerSurvey() &&
    (isAppIosBase() || isDECoursePackEligible() || isAppIOSUpsell())
  );
}

export const shouldHideExistingPurchaseSurveyAnswers = checkSegments({
  allOf: [isPersonalizedPlanTimerExpired],
  noneOf: [isEmailMain, isEmailWinback],
});

export function isEligibleForCheckoutOfferTimer(state = getStore().getState()) {
  return (
    shouldShowCoursePackCheckoutOffers() &&
    !isEligibleForGLP1CEOfferOnly() &&
    Boolean(selectMWCoursepack(state.paymentAddon)) &&
    !isCheckoutOfferTimerExpired()
    // We only check for checkoutTimer expired in the case of checkout offer timer because we don't ever want to
    // re-show the timer once the user already has their offer expired, whereas with the personalized plan,
    // the user is entitled to another round of the timer if they go through the buyfow again
  );
}

export function isEligibleForCheckoutTimer() {
  return (
    !isOnAppleExternalLinkoutPath() &&
    (isEligibleForPersonalizedPlanTimer() || isEligibleForCheckoutOfferTimer())
  );
}

export function isNoomSummitEligible() {
  return false;
}

export const isProgramLocaleNoomSummitEligible = () => {
  // We should make sure we are getting this from the sever context call
  return isEnProgramLocale();
};

export const isNoomSummitDisplayableInStore = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
});

// Users who purchase Noom Summit can still visit the noom-summit-offer pagesets,
// and will be redirected to the confirmation page
export const isNoomSummitOfferEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [
    isTelehealthPlan,
    userHasPurchasedNoomClinical,
    isInExperimentConflictingWithSummitCoursePack,
  ],
});

export const isNoomSummitSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === noomSummitSurveyLayer,
  ],
});

export function hasBoughtMealPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isMealPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isWorkoutPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtMealAndWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.filter(Boolean).some(isMealWorkoutBundle);
}

/**
 * Verifies if current user is on referral routes and wants a full buyflow
 */
export const isFullPersonalizationBuyflow = checkSegments({
  noneOf: [
    ({ surveyAnswers }: CoreReduxState) => {
      return surveyAnswers.customizationChoice?.[0] === "quickStart";
    },
  ],
});

const isPreSelectedPlanLongerThan1Month = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_duration > 1;
};

const isPreSelectedPlanLongerThan2Months = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_duration > 2;
};

const isPreSelectedPlanTrial14Days = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.trial_duration === 14;
};

export const isLoadingPromoCodeModalEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isEmailMain, isEmailWinback],
});

export function shouldRedirectToEmployerSurvey(
  { surveyAnswers } = getStore().getState()
) {
  const correctSurveyAnswer =
    surveyAnswers.employmentLifestyle?.includes("fullTime");
  const isCorrectTargeting = isEnLanguage() && isUS();
  const meristemConfigValue = getMeristemConfigValue(
    "redirectToEmployerSurvey"
  );

  return correctSurveyAnswer && (meristemConfigValue ?? isCorrectTargeting);
}

export function shouldRedirectToPostCheckoutContext() {
  return !!getMeristemConfigValue("usePostCheckoutContext");
}

export function shouldRedirectToHmAddonsContext() {
  return !!getMeristemConfigValue("useHmAddonsContext");
}

export function shouldRedirectHmReferralsContext() {
  return !!getMeristemConfigValue("useHmReferralsContext");
}

export const shouldShowHealthyPaceHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWinback],
});

export const shouldShowImportantEventHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWebWinback],
});

export const isEligibleForNursingOption = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

// ex472b feature. PR: https://github.com/noom/buyflow-client/pull/1356
export const shouldShowAlternatePostCheckoutHeadersText = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const shouldShowUnloadWarning = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const isEligibleForAgingMetabolismSurvey = (
  state = getStore().getState()
) => {
  if (!isEligibleForNX1CourseEnhancements()) {
    return false;
  }
  const { ageRange, cognitiveExplore } = getSurveyAnswers(state);
  if (ageRange?.includes("20s")) {
    return false;
  }
  if (ageRange?.includes("30s")) {
    return (
      cognitiveExplore?.includes("aging") ||
      cognitiveExplore?.includes("metabolism")
    );
  }
  return true;
};

export const isEligibleForSleepAndMovementSurveys = () => {
  return isEligibleForNX1CourseEnhancements();
};

export const isEligibleForModularBuyflow = checkSegments({
  allOf: [isEnLanguage, isMantle],
  noneOf: [isHM],
});

const isEmailIntl = checkSegments({
  allOf: [isIntl],
  oneOf: [isEmailMain, isEmailWinback],
});

const isEmailWinbackUS = checkSegments({
  allOf: [isUS, isEmailWinback],
});

export const isFreeMonthsPlanOverrideEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isEmailIntl, isEmailWinbackUS],
});

export const isFreeMonthsPlanSelectEligible = checkSegments({
  allOf: [isEnLanguage, isEmailMain, isUS],
});

export const shouldShowFreeMonthsPlanSelect = () => {
  return !!freeMonthsOfferConfiguration() && isFreeMonthsPlanSelectEligible();
};

// Free month offers are shown to users in 2 different forms:
// 1. Free month plan override that overrides a shorter plan with a longer plan
// of cheaper or equal price when the user applies a promocode
//
// 2. Free month plan select which shows a 3 plan selection UI (monthly, middle length, long length)
// and discounts the long length plan to make it equal to the price of the middle length plan
// when the user applies a promocode
export const isFreeMonthsOfferEligible = checkSegments({
  oneOf: [isFreeMonthsPlanOverrideEligible, isFreeMonthsPlanSelectEligible],
});

export const isPlanSelectionEligible = checkSegments({
  oneOf: [
    shouldShowFreeMonthsPlanSelect,
    checkSegments({
      allOf: [
        isEmailMain,
        isUS,
        isPreSelectedPlanLongerThan1Month,
        isPreSelectedPlanTrial14Days,
      ],
    }),
  ],
});

export const shouldShowPlanSelectionSavings = checkSegments({
  allOf: [isPlanSelectionEligible, isPreSelectedPlanLongerThan2Months],
});

export function hasExistingAcccount() {
  return (
    !!getActiveUser()?.accessCode ||
    isAppEmail() ||
    disableAccountFieldsForMedFlow()
  );
}

const isCurrentPlanForced = (state: CoreReduxState = getStore().getState()) => {
  const { recommendedPlan } = state;
  const { isForcedPlan } = recommendedPlan;
  return !!isForcedPlan;
};

export const isMultiUserPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isReferralsBuyflow,
    isWinback,
    isAppAndroidBase,
    isAffiliateWithTrial,
  ],
  noneOf: [isHolidayPromotion, isCurrentPlanForced, isEligibleForLongTrial],
});

export function multiUserPlanOptedIn(
  state: CoreReduxState = getStore().getState()
) {
  const TWO_USER_PLAN = "twoUserPlan";
  const { nominationFamily } = state.surveyAnswers;

  return nominationFamily?.[0] === TWO_USER_PLAN;
}

export const shouldShowPrimedSupporterExperience = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isAffiliate,
    isAppBaseRoute,
    isReferralsBuyflow,
    isWebWinback,
    isEmailMain,
    isClinicalRoute,
  ],
});

export const isReferedFromUserPortal = () => {
  return getURLParams()?.redirected_from === "userportal";
};

export const isCampaignFeatureOura = () => {
  return getURLParams()?.feature === "oura";
};

export const isFallbackPromotionalOfferEligible = checkSegments({
  allOf: [isUS, isEnLanguage, isEmailMain],
});

const wasIncorrectPromoCodeEntered = (
  state: CoreReduxState = getStore().getState()
) => {
  return state?.promoCode?.promoCodeErrorType === "IncorrectPromoCode";
};

export const shouldShowFallbackPromotionalOffer = checkSegments({
  allOf: [
    isFallbackPromotionalOfferEligible,
    hasFallbackPromotionalOfferConfiguration,
    wasIncorrectPromoCodeEntered,
  ],
  noneOf: [shouldShowFreeMonthsPlanSelect],
});

export const shouldAllowPromoCodeRemoval = () => {
  if (
    isFallbackPromotionalOfferEligible() &&
    hasFallbackPromotionalOfferConfiguration()
  ) {
    return true;
  }

  return (
    (isPromoCodePersisted() || !!getCampaignFromStorage()) &&
    isEnLanguage() &&
    (isEmailMain() || isEmailWinback())
  );
};

export function canUseShareApi() {
  return canUseNavigatorShare() || canUseMobileSharingInterface();
}

export const isBillingReminderEligible = checkSegments({
  oneOf: [
    isCore,
    isAffiliateWithTrial,
    isAppAndroidBase,
    isReferralsBuyflow,
    isAppIosBase,
    isInAppNativePurchaseEligible,
  ],
  allOf: [isUS, isEnLanguage],
});

export const isEligibleForPreCheckoutFreeMWCPOffer = checkSegments({
  allOf: [isUS, isEnLanguage, isPreSignup],
  oneOf: [isEmailMain, isEmailWinback],
  noneOf: [isReUp],
});

export const isCareFirstRedirectEligible = () => {
  return (
    isUS() &&
    isEnLanguage() &&
    ["MD", "DC", "DE", "VA"].includes(getSubdivision())
  );
};

export const isMHMDAState = () => {
  return isUS() && MHMDA_STATES.includes(getSubdivision());
};

export const isGDPRCountry = () => {
  return isAU() || isCA() || isEU() || isNZ();
};

export const isEligibleForHealthInsuranceProviderQ = (): boolean => {
  const { surveyAnswers } = getStore().getState();
  const { currentBMI } = calculateBMI(surveyAnswers, false);
  return currentBMI > (isUS() ? 21 : 25);
};

export function isHealthInsuranceMedicareOptionEnabled() {
  return (
    getMedFlagOverride("healthInsuranceMedicareOptionEnabledOverwrite") ??
    !!appConfig.HEALTH_INSURANCE_MEDICARE_OPTION_ENABLED
  );
}

/**
 * Whether the user is on the Medicare LP (`noom_entry=medicare_lp`) and
 * answered Medicare screening there (browser session).
 */
export function hasAnsweredMedicareBridgeScreeningLp() {
  if (!isMedicareLpActive()) {
    return false;
  }
  return !!getSessionState("browser").medicareBridgeScreeningLpAnswer;
}

/**
 * Whether the user answered "yes" on `/survey/medicareBridgeScreening`.
 */
export function answeredYesToMedicareBridgeScreening() {
  return getSurveyAnswers()?.medicareBridgeScreening?.includes("yes");
}

export function hasMedicareInsurance() {
  if (!isUS() || !isHealthInsuranceMedicareOptionEnabled()) {
    return false;
  }
  const { hasMedicare } = getSurveyAnswers();

  return hasMedicare ?? false;
}

export function hasMedicareButIneligibleForBridge() {
  return (
    !isFreemiumHealthRoute() &&
    answeredYesToMedicareBridgeScreening() &&
    !isEligibleForMedicareBridge()
  );
}

export function shouldPreselectMedicareOnHealthInsuranceProvider() {
  if (!shouldShowMedicareOptionOnHealthInsuranceProvider()) {
    return false;
  }

  if (isCMSWeightRoute()) {
    return true;
  }

  const { insuranceType, insuranceProvider } = getSurveyAnswers();
  const insuranceTypeValue = Array.isArray(insuranceType)
    ? insuranceType[0]
    : insuranceType;
  const providerStr = String(insuranceProvider ?? "").trim();

  if (providerStr) {
    return providerStr.toLowerCase().includes("medicare");
  }
  return insuranceTypeValue === "medicare";
}

export const isFromCareFirstEmailBlast = () => {
  return (
    isUS() &&
    isEnLanguage() &&
    isFromEmail() &&
    (getURLParams()?.utm_source === "cf_drip" ||
      getURLParams()?.utm_campaign === "cf_blast")
  );
};

export const isUserEnrolledInMABExperiment = () => {
  const mabKey = window.localStorage.getItem(BANDIT_LOCAL_STORAGE_KEY);
  return !!(mabKey && getFeatureFlagValue("enrolledInMABExperiment"));
};

export const isGlpEligible = checkSegments({
  allOf: [isENCoursePackEligible],
  noneOf: [isUK],
  oneOf: [
    isStrictCore,
    isWinback,
    isReferralsBuyflow,
    isAffiliate,
    isClinicalRoute,
    isLegacyAffiliateTrialRoute,
    isAppAndroid,
    isAppEmail,
    isFromNoomWeightEmail,
    isBaseline,
    isAdWordsSEM,
    isFreemiumUpsellRoute,
    isAppIosBaseAndWinback,
  ],
});

export function isOwnSupporter(state: CoreReduxState = getStore().getState()) {
  const { supporterEmail } = getSurveyAnswers(state);
  const { email } = state.userData;

  return email === supporterEmail && !!email;
}

export const shouldUseYesOrNoQuestion = checkSegments({
  allOf: [isEnMantle],
  noneOf: [isEmailWinback, isEmailMain, isClinicalRoute],
});

export const shouldShowWomensHealthSurveyQuestions = checkSegments({
  allOf: [isUS, isFemaleSex],
  oneOf: [isAppIosBase, isFreemiumWeightRoute],
});

export const isCrossSellShopCheckout = () => {
  const { pathname } = window.location;
  return /\/shop\/[^/]+\/checkout/.test(pathname);
};

export const shouldShowActiveSubscriptionImpactBanner = () => {
  return (
    isFreemiumUpsellRoute() || isFreemiumHealthRoute() || isInAppMedUpgrade()
  );
};

export const isOtcBiomarkersInCart = (
  state: CoreReduxState = getStore().getState()
) =>
  isCrossSellShopCheckout() &&
  isShopProductInCart(ShopProduct.OTC_BIOMARKERS, state);

/**
 * Whether the current cross-sell shop checkout is for a subscription product
 * (e.g. metabolic supplements) rather than a one-time purchase (biomarkers).
 *
 * Keyed purely on the entry being a subscription so the auto-renew consent
 * requirement stays in lockstep with the review step, which renders the consent
 * UI for every subscription entry. (A subscription always carries a
 * `recurringBillingCycle`; if it's ever missing the disclaimer reports it via
 * `captureException` rather than silently dropping required consent.)
 */
export const isShopSubscriptionCheckout = (
  state: CoreReduxState = getStore().getState()
) => {
  if (!isCrossSellShopCheckout()) return false;
  const entry = getActiveShopOrderEntry(state);
  return !!entry && isShopSubscriptionEntry(entry);
};

export const isShopContext = () => {
  return getMeristemContext().context_type?.startsWith("shop-");
};

/**
 * Whether the current checkout flow involves shipping a physical product.
 * Controls PO box restrictions, shipping address validation strictness,
 * and address form labeling ("Shipping address" vs "Home address").
 */
export function isPhysicalShipmentFlow() {
  return (
    (isSelectedMedPlanWithMedication() && acceptedNoomClinicalOffer()) ||
    isCrossSellShopCheckout()
  );
}

export const showPaymentInfoAccordion = (
  state: CoreReduxState = getStore().getState()
) => {
  if (showMedCheckout(state)) {
    return true;
  }
  if (isInAppNativePurchaseEligible(state)) {
    // For native purchase eligible users show the payment info accordion
    // only if the plan selection is visible at checkout
    return isApplePurchaseLinkoutEligible(state);
  }
  return !hasVipPromoCode(state);
};

export const showAccountInfoAccordion = (
  state: CoreReduxState = getStore().getState()
) => {
  if (
    isProductSwitchCheckout() ||
    isInAppMedUpgrade() ||
    isB2BUpsellClinicalRoute()
  ) {
    return true;
  }
  return (
    !isInAppNativePurchaseEligible(state) &&
    (shouldEnrollUser(state) || shouldShowNoomClinicalCheckoutOffer(state))
  );
};

export const isEligibleForNewEmailFlow = checkSegments({
  allOf: [isFromNoomWeightEmail, isUS, isEnLanguage],
});

export const showSexQuestionOnEmail = createOneTimeConditionSegment(
  "sex",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.sex;
  }
);

export const showHeightQuestionOnEmail = createOneTimeConditionSegment(
  "basicHeight",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.heightCm;
  }
);

export const showPregnantQuestionOnEmail = createOneTimeConditionSegment(
  "pregnant",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.pregnant || surveyAnswers.pregnant?.[0] === "yes";
  }
);

export const showEatingDisorderQuestionOnEmail = createOneTimeConditionSegment(
  "eatingDisorder",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return (
      !surveyAnswers.eatingDisorder ||
      surveyAnswers.eatingDisorder?.[0] === "yes"
    );
  }
);

export const showEmailQuestionOnEmail = createOneTimeConditionSegment(
  "email",
  (state) => {
    const { userData } = state;
    return !isEmail(userData.email);
  }
);

// Matches any standalone shop pageset layer (e.g. shop-sermorelin, shop-nad).
export const isShopLayer = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      !!getPageSet(linearBuyflow.pageSetName)?.layer?.startsWith("shop-"),
  ],
});

/**
 * Whole health acquisition pixels fire only on the standalone web shop. The
 * post-signup `/offers/shop/` cross-sell (`offers-shop-*` layers) and the app
 * webview are excluded.
 */
export function shouldFireWholeHealthPixels() {
  return isShopLayer() && !isInsideWebView();
}

export const usesLightInterstitialTheme = checkSegments({
  oneOf: [isEnMantle, isShopLayer],
});

// Predicates behind the standalone shop surveys' one-time "is this answer
// still missing" segments. Shared so the sermorelin and NAD+ flows cannot
// drift on what counts as a prefilled answer; each flow snapshots them under
// its own key so running one survey does not freeze the other's.
const isSexAnswerMissing = (state: CoreReduxState) =>
  !getSurveyAnswers(state).sex;

const isGenderAnswerMissing = (state: CoreReduxState) =>
  !getSurveyAnswers(state).gender;

const isDateOfBirthAnswerMissing = (state: CoreReduxState) => {
  const { dateOfBirth, facialBloodScanDateOfBirth } = getSurveyAnswers(state);
  // Free onboarding stores DOB as facialBloodScanDateOfBirth, not dateOfBirth.
  return !(dateOfBirth || facialBloodScanDateOfBirth);
};

const isHeightAnswerMissing = (state: CoreReduxState) =>
  !getSurveyAnswers(state).heightCm;

const isWeightAnswerMissing = (state: CoreReduxState) =>
  !getSurveyAnswers(state).weightKg;

// One-time segments for the sermorelin survey: snapshot whether an answer is
// missing at survey start so the page stays resolvable and counted after
// answering (back nav + stable progress). Prefilled users skip the question.
export const showSexQuestionSermorelin = createOneTimeConditionSegment(
  "sermorelinSex",
  isSexAnswerMissing
);

export const showGenderQuestionSermorelin = createOneTimeConditionSegment(
  "sermorelinGender",
  isGenderAnswerMissing
);

export const showDateOfBirthQuestionSermorelin = createOneTimeConditionSegment(
  "sermorelinDateOfBirth",
  isDateOfBirthAnswerMissing
);

export const showHeightQuestionSermorelin = createOneTimeConditionSegment(
  "sermorelinHeight",
  isHeightAnswerMissing
);

export const isBmiEligibleForSermorelin = (
  { surveyAnswers } = getStore().getState()
) => {
  const { currentBMI } = calculateBMI(surveyAnswers, false);
  return currentBMI >= MINIMUM_NORMAL_BMI_MED;
};

export const isHealthRiskEligibleForSermorelin = (
  { surveyAnswers } = getStore().getState()
) => {
  const answers = surveyAnswers.currentHealthRiskSermorelin ?? [];
  return answers.includes("none");
};

// The same one-time snapshot segments for the NAD+ survey.
export const showSexQuestionNad = createOneTimeConditionSegment(
  "nadSex",
  isSexAnswerMissing
);

export const showGenderQuestionNad = createOneTimeConditionSegment(
  "nadGender",
  isGenderAnswerMissing
);

export const showDateOfBirthQuestionNad = createOneTimeConditionSegment(
  "nadDateOfBirth",
  isDateOfBirthAnswerMissing
);

export const showHeightQuestionNad = createOneTimeConditionSegment(
  "nadHeight",
  isHeightAnswerMissing
);

/**
 * Unlike sermorelin, which always asks for weight, NAD+ asks only when we have
 * no weight on file: weight is collected for the patient record but is not an
 * NAD+ eligibility criterion, so there is no ineligible state behind it.
 */
export const showWeightQuestionNad = createOneTimeConditionSegment(
  "nadWeight",
  isWeightAnswerMissing
);

export const isHealthRiskEligibleForNad = (
  { surveyAnswers } = getStore().getState()
) => {
  const answers = surveyAnswers.currentHealthRiskNad ?? [];
  return answers.includes("none");
};

// The same one-time snapshot segments for the Oxytocin survey.
export const showSexQuestionOxytocin = createOneTimeConditionSegment(
  "oxytocinSex",
  isSexAnswerMissing
);

export const showGenderQuestionOxytocin = createOneTimeConditionSegment(
  "oxytocinGender",
  isGenderAnswerMissing
);

export const showDateOfBirthQuestionOxytocin = createOneTimeConditionSegment(
  "oxytocinDateOfBirth",
  isDateOfBirthAnswerMissing
);

export const showHeightQuestionOxytocin = createOneTimeConditionSegment(
  "oxytocinHeight",
  isHeightAnswerMissing
);

/**
 * Like NAD+, Oxytocin asks for weight only when we have no weight on file:
 * weight is collected for the patient record but is not an Oxytocin eligibility
 * criterion, so there is no ineligible state behind it.
 */
export const showWeightQuestionOxytocin = createOneTimeConditionSegment(
  "oxytocinWeight",
  isWeightAnswerMissing
);

export const isHealthRiskEligibleForOxytocin = (
  { surveyAnswers } = getStore().getState()
) => {
  const answers = surveyAnswers.currentHealthRiskOxytocin ?? [];
  return answers.includes("none");
};

export function shouldDisplayWLGModal() {
  return (
    isUS() &&
    isChooseTrialPriceNewStyleEligible() &&
    !isHM() &&
    !getFeatureFlagValue("disableWLGModal") &&
    isClinicalRoute()
  );
}

export function shouldShowExNV52() {
  return !!getMeristemConfigValue("isExNV52Active");
}

export function shouldShowTelex225V2() {
  return !!getMeristemConfigValue("isTelex225V2Active");
}

export function shouldShowEx1608PrivacyPolicy() {
  return !!getMeristemConfigValue("updatedFacialBloodScanPrivacyPolicy");
}

export function shouldShowEx1608UpsellCta() {
  return !!getMeristemConfigValue("updatedUpsellCta");
}

export function isDiabetesSupportActive() {
  return (
    !!getMeristemConfigValue("isDiabetesSupportActive") &&
    !isInAppNativePurchaseEligible()
  );
}

export const isProbioticsDeliveryEligible = checkSegments({
  // The Pendulum cross-sell is retired. The targeting below is kept to define
  // who should get the Metabolic replacement (MGEXP-4104).
  allOf: [() => false, isEnLanguage, isUS],
  oneOf: [
    isCore,
    isWinback,
    isReferralsRoute,
    isFromNoomWeightEmail,
    isAffiliateWithTrial,
    isAppAndroidBase,
  ],
});

export function shouldSuppressStepTrackingVibeOffer() {
  return shouldSuppressVibeOffer(STEP_TRACKING_VIBE_OFFER_ID);
}

export function isEligibleForStepTrackingVibeOffer() {
  const { surveyAnswers } = getStore().getState();

  return (
    ((surveyAnswers.stepTracking?.[0] === "yes" && surveyAnswers.dailySteps) ||
      surveyAnswers.stepTracking?.[0] === "no") &&
    !isClinicalRoute() &&
    isEnLanguage()
  );
}

export function shouldShowStepTrackingVibeOffer() {
  return (
    isEligibleForStepTrackingVibeOffer() &&
    !shouldSuppressStepTrackingVibeOffer()
  );
}

export function isEligibleForNoomPremiumNoTrial() {
  const meristemFlag = getMeristemConfigValue("premiumSubForNoTrialHw");
  return !(meristemFlag || isPremiumTextCoachingSubscriptionEligible());
}

export function shouldSuppressGLP1Question() {
  return hasAnsweredToGLP1FollowUpInCurrentSession("no");
}

export function shouldSuppressPreviousMedicationsQuestion() {
  return (
    hasAnsweredToGLP1FollowUpInCurrentSession("no") ||
    hasAnsweredPlanInterestInCurrentSession("no")
  );
}

export function isSubscriptionPremiumTier() {
  const { serverContext } = getStore().getState();
  return isPremiumTierPlanId(serverContext.current_plan_id);
}

export function isSubscriptionMedTier() {
  const { serverContext } = getStore().getState();
  return isMedTierPlanId(serverContext.current_plan_id);
}

export function isSubscriptionWholeHealth() {
  return !!getSubscribedShopEntry();
}

export function isSubscriptionWholeHealthMed() {
  return !!getSubscribedShopEntry()?.isMedication;
}

export function isPlanLongerThanOneMonth() {
  const { serverContext } = getStore().getState();
  const { plan_duration, plan_duration_units } = serverContext;
  if (!plan_duration || !plan_duration_units) return false;
  if (plan_duration_units === "MONTHS") return plan_duration > 1;
  if (plan_duration_units === "DAYS") return plan_duration > 30;
  if (plan_duration_units === "WEEKS") return plan_duration > 4;
  if (plan_duration_units === "YEARS") return true;
  return false;
}

export function isSubscriptionCompoundedTirzepatideMedTier() {
  const { serverContext } = getStore().getState();
  return isCompoundedTirzepatideMedTier(serverContext.current_plan_id);
}

export function userHasActivePremiumWithTextCoachingAddon() {
  const { serverContext } = getStore().getState();
  return !!serverContext.addon_subscriptions?.find((addon) => {
    return (
      addon.product === "PREMIUM_WITH_TEXT_COACHING" &&
      addon.status === "ACTIVE"
    );
  });
}

export function userHasActivePremiumBundleAddon() {
  const { serverContext } = getStore().getState();
  return !!serverContext.addon_subscriptions?.find((addon) => {
    return (
      addon.product === "MEAL_WORKOUT_PLANNING" && addon.status === "ACTIVE"
    );
  });
}

export function userHasPremiumPlan() {
  return (
    isSubscriptionPremiumTier() ||
    userHasActivePremiumWithTextCoachingAddon() ||
    userHasActivePremiumBundleAddon()
  );
}

export function userHasActiveSubscription() {
  const { currentSubscriptionData } = getStore().getState();
  return currentSubscriptionData.hasActiveSubscription;
}

userHasActiveSubscription.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

export function shouldShowZipcodeLoadError(
  state: CoreReduxState = getStore().getState()
) {
  if (!getActiveUser()?.accessCode) {
    return false;
  }

  const { billingData } = state;

  return (
    isRequestFailed(billingData?.activeSubscription) ||
    isRequestFailed(billingData?.homeAddress)
  );
}

export function shouldShowZipcodeQuestion(
  state: CoreReduxState = getStore().getState()
) {
  if (!getActiveUser()?.accessCode) {
    return true;
  }

  if (shouldShowZipcodeLoadError(state)) {
    return false;
  }

  const { billingData } = state;

  return !(
    hasBillingActiveSubscription(billingData) &&
    getSurveyZipcodeFromHomeAddress(billingData?.homeAddress?.data)
  );
}

shouldShowZipcodeQuestion.load = () =>
  Promise.all([
    getStore().dispatch(loadActiveSubscription()),
    getStore().dispatch(loadBillingUserHomeAddress()),
  ]);

export function getMedFlagOverride(medFlag: string) {
  const urlParams = getURLParams();
  if (isDevMode() && urlParams && urlParams[medFlag] != null) {
    return urlParams[medFlag] === "true";
  }
  return undefined;
}

export function getIsMultiProductOverride() {
  return getMedFlagOverride("isMultiProduct");
}

export function shouldHideFitonWorkoutPlanIntensity() {
  const { surveyAnswers } = getStore().getState();

  return (
    (isMaleGender() &&
      surveyAnswers.fitonWorkoutPlanDumbbells?.includes("hasNoDumbbells")) ||
    (!isMaleGender() &&
      surveyAnswers.fitonWorkoutPlanDuration?.includes("lessThan20Min"))
  );
}

export function isZipcodeCorrespondingToUSState(
  { surveyAnswers } = getStore().getState()
) {
  const { zipcode } = surveyAnswers;
  if (zipcode !== undefined) {
    const state = zipState(zipcode);
    return state !== null;
  }
  return false;
}

export function isEligibleForMedProducts() {
  return getEligibleMedProducts().length > 0;
}

export function isEligibleForMedProductsInMedBranchedFlow() {
  return (
    isEligibleForMedProducts() &&
    isUserSegmentEligibleForMedSwitchBuyflow() &&
    isAgeRangeStatusNoomClinicalEligible() &&
    isPregnantStatusNoomClinicalEligible()
  );
}

export function isEligibleForMoreThanOneMedProduct() {
  return getEligibleMedProducts().length > 1;
}

export function isEligibleForMedSku(sku: MED_SKU) {
  return getEligibleMedProducts().includes(sku);
}

export function hasSelectedBrandedPlan() {
  return (
    getMedFlagOverride("selectBrandedPlan") ??
    getSessionState("browser").selectedMedPlanType === "branded"
  );
}

export function hasSelectedCompoundedPlan() {
  return (
    getMedFlagOverride("selectCompoundedPlan") ??
    getSessionState("browser").selectedMedPlanType === "compounded"
  );
}

export function hasSelectedCompoundedTirzepatidePlan() {
  return (
    getMedFlagOverride("selectCompoundedTirzepatidePlan") ??
    getSessionState("browser").selectedMedPlanType === "compoundedTirzepatide"
  );
}

export function hasSelectedCompoundedMicrodoseTirzepatidePlan() {
  return (
    getMedFlagOverride("selectCompoundedMicrodoseTirzepatidePlan") ??
    getSessionState("browser").selectedMedPlanType ===
      "compoundedMicrodoseTirzepatide"
  );
}

export function hasSelectedOralsMetforminPlan() {
  return (
    getMedFlagOverride("selectOralsMetforminPlan") ??
    getSessionState("browser").selectedMedPlanType === "oralsMetformin"
  );
}

export function hasSelectedZepboundVialPlan() {
  return (
    getMedFlagOverride("selectZepboundVialPlan") ??
    getSessionState("browser").selectedMedPlanType === "brandedZepboundVial"
  );
}

export function hasSelectedWegovyPillPlan() {
  return (
    getMedFlagOverride("selectWegovyPillPlan") ??
    getSessionState("browser").selectedMedPlanType === "brandedWegovyPill"
  );
}

export function hasSelectedLiraglutidePlan() {
  return (
    getMedFlagOverride("selectLiraglutidePlan") ??
    getSessionState("browser").selectedMedPlanType === "genericLiraglutide"
  );
}

export function hasSelectedMicrodosePlan() {
  return (
    getMedFlagOverride("selectMicrodosePlan") ??
    getSessionState("browser").selectedMedPlanType === "compoundedMicrodose"
  );
}

export function hasSelectedMicrodoseBiomarkerPlan() {
  return (
    getMedFlagOverride("selectMicrodoseBiomarkerPlan") ??
    getSessionState("browser").selectedMedPlanType ===
      "compoundedMicrodoseProactiveHealthBiomarker"
  );
}

export function hasSelectedNoomWeightPlan() {
  return !!getSessionState("browser").selectedNoomWeightPlanOption;
}

const NMC_CONTEXT_TYPES = ["nmc-landing", "nmc-survey", "nmc-coming-soon"];

export function isNmc() {
  return NMC_CONTEXT_TYPES.includes(getMeristemContext().context_type);
}

export function isNoomPremiumInAppUpgradeEligible() {
  const { planId, activeSubscriptionCurriculumSlug, isIAPsubscription } =
    getLoadedSubscriptionStatus() || {};

  return (
    !isMultiUserPlan(planId) &&
    !isIAPsubscription &&
    !planId?.endsWith("__pause") &&
    activeSubscriptionCurriculumSlug?.startsWith(
      HEALTHY_WEIGHT_CURRICULUM_PREFIX
    )
  );
}

isNoomPremiumInAppUpgradeEligible.load = async () => {
  await loadSubscriptionStatus();
};

export function isCompoundedPlanIneligible() {
  const params = new URLSearchParams(window.location.search);
  const forcedIneligibility = params.get("disableCompoundedOffer") === "true";

  return (
    forcedIneligibility ||
    !getEligibleMedProducts().includes(MED_SKU.COMPOUNDED)
  );
}

export function isLiraglutidePlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.GENERIC_LIRAGLUTIDE);
}

export function isCompoundedAndLiraglutidePlanIneligible() {
  return isCompoundedPlanIneligible() && isLiraglutidePlanIneligible();
}

export function isMicrodosePlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.COMPOUNDED_MICRODOSE);
}

export function isPremiumConfigurationWithCoaching() {
  const { planId } = getLoadedSubscriptionStatus() || {};

  return isPremiumTierPlanId(planId);
}

isPremiumConfigurationWithCoaching.load = async () => {
  await loadSubscriptionStatus();
};

export function isPremiumUpgradeSourceBuyflow() {
  const { userData } = getStore().getState();
  return userData?.premiumUpgradeSource === "BUYFLOW";
}

export const isPremiumTierCounterOfferEligible = checkSegments({
  allOf: [isSubscriptionPremiumTier, isPremiumUpgradeSourceBuyflow],
  noneOf: [isSubscriptionMedTier, isSubscriptionInvalid],
});

export function isBrandedPlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.BRANDED);
}

export function isZepboundVialPlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.BRANDED_ZEPBOUND_VIAL);
}

export function isWegovyPillPlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.BRANDED_WEGOVY_PILL);
}

export function isOralsPlanIneligible() {
  return !getEligibleMedProducts().includes(MED_SKU.ORALS_METFORMIN);
}

export function hasDiabetesType2HealthRisk() {
  return getMedicalHealthRisks().includes("type2Diabetes");
}

export const showHeightQuestionOnInAppUpgrade = createOneTimeConditionSegment(
  "basicHeight",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.heightCm;
  }
);

export const showIdealWeightQuestionOnInAppUpgrade =
  createOneTimeConditionSegment(
    "idealWeightKg",
    (state) => !hasIdealWeight(state)
  );

export function hasIdealWeight(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return !!surveyAnswers.idealWeightKg;
}

export const showSexQuestionOnInAppUpgrade = createOneTimeConditionSegment(
  "sex",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    return !surveyAnswers.sex;
  }
);

export const showSexQuestionOnUpsell = createOneTimeConditionSegment(
  "sexUpsell",
  (state) => {
    const surveyAnswers = getSurveyAnswers(state);
    // Don't show if user already answered sex
    if (surveyAnswers.sex) {
      return false;
    }
    // Only show if user didn't answer gender, or didn't pick male or female
    const gender = surveyAnswers.gender?.[0];
    return (
      !gender ||
      (gender !== genderConstants.male && gender !== genderConstants.female)
    );
  }
);

export function isIneligibleForMedUpgrade() {
  const { serverContext } = getStore().getState();

  if (!serverContext.is_eligible_for_med_upgrade) {
    return true;
  }

  const eligibleMedProducts = getEligibleMedProducts({
    checkBMI: false,
    checkHealthRisks: false,
    checkState: true,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    disabledNamedChecks: [
      "isQualifiedForPD",
      "isNotEligibleForCompoundedSemaglutide",
    ],
  });
  const { selectedMedPlanType } = getSessionState("browser");
  const selectedMedSku = getSelectedMedPlanType();

  return selectedMedPlanType
    ? !eligibleMedProducts?.includes(selectedMedSku)
    : eligibleMedProducts?.length === 0;
}

export function isEligibleForNoomPremiumInMedUpgradeFlow() {
  const { serverContext } = getStore().getState();
  return (
    serverContext.is_eligible_for_premium_upgrade && !!getInAppAutoCookie()
  );
}

export const isEligibleForFreemiumIUBuyflow = checkSegments({
  oneOf: [isUS, isCA, isAU],
  allOf: [isFreemiumIdentifiedRoute, isEnLanguage],
});

export const isEligibleForFreemiumUnidentifiedBuyflow = checkSegments({
  oneOf: [isAppUS, isAppCA, isAppAU],
  allOf: [isFreemiumUnidentifiedRoute, isEnLanguage],
});

// checks if any freemium route
// (unidentified user who wants free bodyscan experience should be added here)
export const isEligibleForFreemium = checkSegments({
  oneOf: [
    isEligibleForFreemiumIUBuyflow,
    isEligibleForFreemiumUnidentifiedBuyflow,
  ],
});

export const isEligibleForUSModularMainSurvey = checkSegments({
  allOf: [isUS, isEligibleForModularBuyflow],
  noneOf: [
    isHM,
    isEligibleForNewEmailFlow,
    isEligibleForClinicalBuyflow,
    isEligibleForFreemium,
    isEligibleForFreemiumWeightBuyflow,
    isEligibleForStandaloneHRTBuyflow,
  ],
});

export function shouldShowMedicareOptionOnHealthInsuranceProvider() {
  if (!isHealthInsuranceMedicareOptionEnabled()) {
    return false;
  }

  return isEligibleForUSModularMainSurvey();
}

export const isFacialBloodScanSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer === "facial-blood-scan",
  ],
});

export const isAppFreeOnboardingFacialBloodScanSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      getPageSet(linearBuyflow.pageSetName)?.layer ===
      "app-free-onboarding-facial-blood-scan",
  ],
});

export function shouldShowFirstNameQuestion() {
  // Apple requires that the firstName question is skipped
  // if users sign in with Apple. Backend will set inAppName in
  // the userData for this flow so we can use it to identify and skip.
  // See https://noomhq.atlassian.net/browse/GTIER-166.
  const { userData } = getStore().getState();
  return !isEligibleForFreemiumUnidentifiedBuyflow() && !userData.inAppName;
}

export function isRedesignEnabled() {
  return (
    !isClinicalRoute() &&
    (isEligibleForFreemiumIUBuyflow() ||
      isFreemiumQuizRoute() ||
      isFacialBloodScanSurvey())
  );
}

export function isNoPaidTraffic() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === null;
}

export function isOralsMedPaidTraffic() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === FEATURE_PARAM.ORALS;
}

export function getMedSwitchFlowToParam() {
  const params = new URLSearchParams(window.location.search);
  return params.get("switchTo")?.toUpperCase();
}

export function shouldFetchAuthenticatedUserData() {
  return !!getActiveUser()?.accessCode && (isUpsellRoute() || isShopContext());
}

export function isEligibleForNoomMedSwitch() {
  const switchToParam = getMedSwitchFlowToParam();

  const isValidSwitchToParam =
    switchToParam &&
    ELIGIBLE_SKUS_FOR_SWITCH.includes(switchToParam as MED_SKU);
  return isValidSwitchToParam;
}

export function getMhtForcedParam() {
  const params = new URLSearchParams(window.location.search);
  return params.get("enableMhtFlow");
}

export function isHRTPromoLPTraffic() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === "hrt";
}

export const isRouteEligibleForMhtBuyflow = checkSegments({
  oneOf: [
    isCore,
    isAffiliateWithTrial,
    isReferralsBuyflow,
    isWebWinback,
    isEmailMain,
    isAppAndroidBase,
  ],
});

export const isUserSegmentEligibleForMhtBuyflow = checkSegments({
  allOf: [isUS, isEnLanguage, isRouteEligibleForMhtBuyflow],
});

export const isEligibleForHRTBuyflow = checkSegments({
  allOf: [isUserSegmentEligibleForMhtBuyflow, isFemaleSex],
});

export const willReceiveWomensWeightLossModule = (
  { surveyAnswers } = getStore().getState()
) => {
  const { menopauseStatus, ageRange, menstrualCycle, periodEndReason } =
    surveyAnswers;
  if (isEligibleForHRTBuyflow()) {
    return (
      ["30s", "40s"].includes(ageRange?.[0]) &&
      (menstrualCycle?.[0] === "yes" ||
        (menstrualCycle?.[0] === "no" &&
          periodEndReason?.includes("hormonalContraceptive")))
    );
  }
  // Note(James): Any answer to this question will result in the module, so the
  // eligibility is really "anyone who can see the question" - but leaving this segment to be explicit
  return [
    "preMenopause",
    "perimenopause",
    "menopause",
    "postMenopause",
    "preferNotToSay",
  ].includes(menopauseStatus?.[0]);
};

export const willReceiveSomeWomensHealthModule = checkSegments({
  oneOf: [willReceiveWomensWeightLossModule, willReceiveMenstrualCycleModule],
});

export function isAgeRangeEligibleForHRT() {
  const { ageRange, HRTAgeRange40s, ageYears, ageRangeHRT } =
    getSurveyAnswers();

  if (isAppBaseRoute() && ageYears) {
    return isAgeEligibleForMht(ageYears);
  }

  if (isEligibleForStandaloneHRTBuyflow() && ageRangeHRT?.[0]) {
    return ["45-49", "50s"].includes(ageRangeHRT?.[0]);
  }

  return ageRange?.[0] === "50s" || HRTAgeRange40s?.[0] === "yes";
}

export function isLastPeriodEligibleForHRT() {
  const { lastPeriod } = getSurveyAnswers();

  return ["under1Year", "from1To2Years", "from2To10Years"].includes(
    lastPeriod?.[0]
  );
}

export function isPeriodEndReasonEligibleForHRT() {
  const { periodEndReason } = getSurveyAnswers();
  return !periodEndReason?.includes("hormonalContraceptive");
}

export function isMenopauseSymptomsEligibleForHRT() {
  const { menopauseSymptoms } = getSurveyAnswers();
  const validSymptoms = [
    MenopauseSymptoms.HOT_FLASHES,
    MenopauseSymptoms.NIGHT_SWEATS,
    MenopauseSymptoms.SLEEP_DISTURBANCES,
    MenopauseSymptoms.BRAIN_FOG,
    MenopauseSymptoms.CHANGES_IN_MOOD,
    MenopauseSymptoms.JOINT_PAIN,
  ];
  return menopauseSymptoms?.some((symptom) =>
    validSymptoms.includes(symptom as MenopauseSymptoms)
  );
}

export function isMoreThanOneMenopauseSymptomSelected() {
  return (
    getTopSymptomsBasedOnUserSelected(
      Object.keys(MenopauseSymptoms).length,
      true,
      false
    ).length > 1
  );
}

// Note (Anastasiya): it's only reliable if you call it on the checkout page since the slice might be gone after page is refreshed after
export function isStateEligibleForMhtPostCheckoutBuyflow(
  state = getStore().getState()
) {
  const { checkout } = state;
  const { zipcode } = checkout.billingAddress;
  if (zipcode !== undefined) {
    const userState = zipState(zipcode);
    return !appConfig.HRT_INELIGIBLE_US_STATES.includes(userState);
  }
  return false;
}

// Note (Anastasiya): it's only reliable if you call it on the checkout page since the slice might be gone after page is refreshed after
export function isSelectedPlanEligibleForMhtPostCheckoutBuyflow(
  state = getStore().getState()
) {
  const { checkout } = state;
  const isMultiUserPlanPurchased = checkout.multiUserPlan?.toggled;
  return !isMultiUserPlanPurchased;
}

export const isEligibleForMhtPostCheckoutBuyflow = checkSegments({
  allOf: [
    isEligibleForHRTBuyflow,
    isAgeRangeEligibleForHRT,
    isLastPeriodEligibleForHRT,
    isPeriodEndReasonEligibleForHRT,
    isMenopauseSymptomsEligibleForHRT,
    isPregnantStatusEligibleForHRT,
  ],
  noneOf: [hasSignedLiabilityWaiver],
});

export function getSelectedMedPlanType(): MED_SKU {
  if (hasSelectedOralsMetforminPlan()) {
    return MED_SKU.ORALS_METFORMIN;
  }

  if (hasSelectedCompoundedPlan()) {
    return MED_SKU.COMPOUNDED;
  }

  if (hasSelectedCompoundedTirzepatidePlan()) {
    return MED_SKU.COMPOUNDED_TIRZEPATIDE;
  }

  if (hasSelectedCompoundedMicrodoseTirzepatidePlan()) {
    return MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE;
  }

  if (hasSelectedHRTCreamPlan()) {
    return MED_SKU.HRT_CREAM;
  }

  if (hasSelectedHRTPatchPlan()) {
    return MED_SKU.HRT_PATCH;
  }

  if (hasSelectedZepboundVialPlan()) {
    return MED_SKU.BRANDED_ZEPBOUND_VIAL;
  }

  if (hasSelectedWegovyPillPlan()) {
    return MED_SKU.BRANDED_WEGOVY_PILL;
  }

  if (hasSelectedLiraglutidePlan()) {
    return MED_SKU.GENERIC_LIRAGLUTIDE;
  }

  if (hasSelectedMicrodosePlan()) {
    return MED_SKU.COMPOUNDED_MICRODOSE;
  }

  if (hasSelectedMicrodoseBiomarkerPlan()) {
    return MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER;
  }

  if (getSessionState("browser").selectedMedPlanType === "membership") {
    return MED_SKU.MEMBERSHIP;
  }

  return MED_SKU.BRANDED;
}

export function isGhostPromoEligiblePlan() {
  const { checkout } = getStore().getState();
  if (!checkout?.acceptedClinicalOffer) {
    return false;
  }
  return MED_WEIGHT_LOSS_PLAN_SKUS.includes(getSelectedMedPlanType());
}

export function isMedDripPromoEligiblePlan() {
  const { checkout } = getStore().getState();
  if (!checkout?.acceptedClinicalOffer) {
    return false;
  }
  return MED_DRIP_PROMO_ELIGIBLE_PLAN_SKUS.includes(getSelectedMedPlanType());
}

export function isMayoPromoEligiblePlan() {
  const { checkout } = getStore().getState();
  if (!checkout?.acceptedClinicalOffer) {
    return false;
  }
  return MED_WEIGHT_LOSS_PLAN_SKUS.includes(getSelectedMedPlanType());
}

export function isCentenePromoEligiblePlan() {
  const { checkout } = getStore().getState();
  if (!checkout?.acceptedClinicalOffer) {
    return true;
  }
  return MED_WEIGHT_LOSS_PLAN_SKUS.includes(getSelectedMedPlanType());
}

export function isSelectedMedPlanWithMedication(sku?: MED_SKU) {
  const selectedMedPlanType = sku || getSelectedMedPlanType();

  return [
    MED_SKU.COMPOUNDED,
    MED_SKU.COMPOUNDED_TIRZEPATIDE,
    MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
    MED_SKU.ORALS_METFORMIN,
    MED_SKU.HRT_CREAM,
    MED_SKU.HRT_PATCH,
    MED_SKU.GENERIC_LIRAGLUTIDE,
    MED_SKU.COMPOUNDED_MICRODOSE,
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
  ].includes(selectedMedPlanType);
}

export const isInAppChatLobbyUpsell = () => {
  const urlParams = getURLParams();
  return urlParams?.utm_source === "chatlobby";
};

export const isInAppFitOnUpsell = () => {
  const urlParams = getURLParams();
  return urlParams?.utm_source === "fiton";
};

export function isIdealWeightConfirmationOnFreemiumLPEligible() {
  const { idealWeightKg } = getSurveyAnswers();
  const landingBMI = getLandingPageBMIs({ setIfMissing: true });
  const belowMinimumBMI =
    !!landingBMI && belowMinimumNormalBMI(landingBMI?.targetBMI, true);
  return isUpsellRoute() && !!idealWeightKg && !belowMinimumBMI;
}

export function isFreemiumUpsellDuringOnboarding() {
  const { freemiumOnboardingSourceId } = getSessionState("browser");
  return (
    isFreemiumUpsellRoute() &&
    freemiumOnboardingSourceId === "app-free-onboarding"
  );
}

export function showIdealWeightQuestionOnFreemium() {
  const { surveyAnswers } = getStore().getState();
  const { idealWeightConfirmationFreemium } = surveyAnswers;
  return (
    !idealWeightConfirmationFreemium?.includes("yes") ||
    !isIdealWeightConfirmationOnFreemiumLPEligible()
  );
}

export function shouldShowIdealWeightQuestionOnUpsellClinical() {
  if (!showIdealWeightQuestionOnFreemium()) {
    return false;
  }

  // For FreemiumHealthRoute, also require hasWeightLossGoalBehavior
  if (isFreemiumHealthRoute()) {
    return hasWeightLossGoalBehavior();
  }

  // For other routes, just use showIdealWeightQuestionOnFreemium
  return true;
}

export function shouldShowSurveyHeaderExitButton() {
  return isEligibleForMhtPostCheckoutBuyflow() && !isGlobalExitEnabled();
}

export function isFromMealbot() {
  const { fromMealbot } = getURLParams();
  return fromMealbot === "true";
}

export function isMealPlanAllergyQuestionEnabled() {
  return !!appConfig.MEAL_PLAN_ALLERGY_QUESTION_ENABLED;
}

export function isMealPlanFavoriteCuisineQuestionEnabled() {
  return !!appConfig.MEAL_PLAN_FAVORITE_CUISINE_QUESTION_ENABLED;
}

export function isMealPlanMaxCookingTimeQuestionEnabled() {
  return !!appConfig.MEAL_PLAN_MAX_COOKING_TIME_QUESTION_ENABLED;
}

export const isEligibleForPromoCampaign = checkSegments({
  noneOf: [isClinicalRoute],
});

export function isSideNavNoomMedUpgrade() {
  return (
    isInAppMedUpgrade() &&
    visitTracker.getTrackingParams().utm_source === "sidenav"
  );
}

const metaTrafficSources = ["fb", "ig", "an"];

export function isMetaTraffic() {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams?.get("utm_source");
  const trafficSource = urlParams?.get("traffic_source");
  return (
    metaTrafficSources.includes(utmSource) ||
    metaTrafficSources.includes(trafficSource)
  );
}

export function hasSelectedHRTCreamPlan() {
  return (
    getMedFlagOverride("selectHRTCreamPlan") ??
    getSessionState("browser").selectedHRTPlanType === "cream"
  );
}

export function userHasWeightLossGoal(state = getStore().getState()) {
  const healthGoalBehavior = getSurveyAnswers(state)?.healthGoalBehavior;
  return (
    !healthGoalBehavior?.length ||
    healthGoalBehavior.includes("loseWeight") ||
    (healthGoalBehavior.length === 1 && healthGoalBehavior.includes("notSure"))
  );
}

export function isFromMealFeedbackScreenWithWeightLossGoal(): boolean {
  // Use current URL parameters, not stored/persisted tracking params
  const urlParams = getURLParams();
  const utmSource = urlParams?.utm_source;

  // Check if user came from meal feedback screens (current URL only)
  const fromMealFeedback = [
    "meal_feedback_med_upgrade",
    "meal_feedback_weight_upgrade",
    "meal_feedback_content_med_upgrade",
    "meal_feedback_content_weight_upgrade",
    "meal_logging_method_selector_history_med_upgrade",
    "meal_logging_method_selector_history_weight_upgrade",
    "meal_logging_method_selector_barcode_med_upgrade",
    "meal_logging_method_selector_barcode_weight_upgrade",
    "meal_logging_method_selector_search_med_upgrade",
    "meal_logging_method_selector_search_weight_upgrade",
  ].includes(utmSource);

  return fromMealFeedback && userHasWeightLossGoal();
}

export function hasSelectedHRTPatchPlan() {
  return (
    getMedFlagOverride("selectHRTPatchPlan") ??
    getSessionState("browser").selectedHRTPlanType === "patch"
  );
}

// Based on MHT pre-checkout flow
export function isUterusNotRemoved() {
  const { periodEndReason } = getSurveyAnswers();
  return !periodEndReason?.includes("noUterus");
}

export const isHRTSurvey = checkSegments({
  allOf: [
    ({ linearBuyflow }) =>
      [
        hrtSurveyLayer,
        hrtPaymentResultsLayer,
        hrtPaymentCheckoutLayer,
      ].includes(getPageSet(linearBuyflow.pageSetName)?.layer),
  ],
});

export function isTelehealthHRTIntakeTheme(): boolean {
  const themeString =
    getNoomSessionStorage(SessionStorageKey.telehealthIntakeTheme) ?? null;

  // If session storage doesn't have the stored value search browser URL
  const theme: TelehealthIntakeTheme =
    themeString === null
      ? TelehealthIntakeTheme[getURLParams()?.theme]
      : TelehealthIntakeTheme[themeString];

  return theme === TelehealthIntakeTheme.HRT;
}

export const shouldShowMenopauseBoxOnChoosePlan = checkSegments({
  allOf: [
    isHRTPromoLPTraffic,
    isEligibleForHRTBuyflow,
    isPregnantStatusEligibleForHRT,
    isAgeRangeEligibleForHRT,
  ],
});

export const shouldShowMenopauseBoxOnCheckout = checkSegments({
  allOf: [isHRTPromoLPTraffic, isEligibleForMhtPostCheckoutBuyflow],
});

export function shouldHideBackButton() {
  const urlParams = getURLParams();
  return urlParams?.hideBackButton === "true";
}

export function isFlowEligibleForClinicalProducts() {
  return (
    isClinicalRoute() ||
    isInAppMedUpgrade() ||
    isNoomWeightMedBranching() ||
    isHealthRoute() ||
    isInsuranceCheckerRoute()
  );
}

export function isFlowEligibleForHRTProducts() {
  return isHRTSurvey() || isEligibleForStandaloneHRTBuyflow();
}

export function isHRTPatchEnabled() {
  return appConfig.HRT_PATCH_ENABLED;
}

export function isEligibleForHrtAfterZipcodeQuestion() {
  return (
    getEligibleMedProducts(
      {
        checkBMI: false,
        checkHealthRisks: false,
        checkState: true,
        checkFlow: true,
        requirePreviousMedicationsAnswer: false,
        requireHighestBMIAnswer: false,
      },
      MED_FLOW.HRT
    ).length > 0
  );
}

export function isEligibleForHrtAfterHealthRiskQuestion() {
  return (
    getEligibleMedProducts(
      {
        checkBMI: false,
        checkHealthRisks: true,
        checkState: false,
        checkFlow: true,
        requirePreviousMedicationsAnswer: false,
        requireHighestBMIAnswer: false,
      },
      MED_FLOW.HRT
    ).length > 0
  );
}

const isHRTLink = checkSegments({
  allOf: [() => window.location.pathname.includes("/hrt-")],
});

export const shouldFireMedPixel = checkSegments({
  oneOf: [
    isWebClinicalRoute,
    isHRTRoute,
    isHRTLink,
    isHealthRoute,
    isInsuranceCheckerRoute,
  ],
});

export const isRecommendedForZepboundVialAfterInsuranceCoverageQuestion =
  () => {
    const { surveyAnswers } = getStore().getState();
    const { insuranceCoverage } = surveyAnswers;

    return insuranceCoverage?.[0] === "no";
  };

export const shouldRecommendZepbound = () => {
  const { zepboundMedicationPreference } = getSurveyAnswers();
  return zepboundMedicationPreference?.[0] === "no";
};

export const hasExpressedInterestInNonOralsMedication = () => {
  const { oralsMedicationPreference, oralsPlanPreference } = getSurveyAnswers();

  // If user is in the new experiment, use the new logic
  if (oralsPlanPreference) {
    return (
      oralsPlanPreference === "GLP1" || oralsPlanPreference === "RECOMMENDATION"
    );
  }

  // Fallback to old logic for users not in experiment
  return oralsMedicationPreference?.[0] === "yes";
};

/**
 * True when the async fulfillment variant is available AND the user actually
 * qualifies for at least one branded SKU (BMI, health risks, branded state
 * list, flow checks). Without the SKU check, async-variant availability alone
 * could route users into branded-only flows (e.g. the Medicare bridge
 * interstitial) when no branded plan can be offered to them.
 */
export const isEligibleForBrandedAsync = () => {
  return (
    getEligibleMedProducts().includes(MED_SKU.BRANDED) &&
    getEligibleBrandedVariants().includes(BRANDED_VARIANT.ASYNC)
  );
};

export function isTelex264Active() {
  return !!getMeristemConfigValue("isTelex264Active");
}

export const isFreemiumPageset = (pageSet: PageSet) => {
  return (
    pageSet.id === "USFreemiumUnidentifiedUserMainSurvey" ||
    pageSet.id === "USFreemiumIdentifiedUserMainSurvey"
  );
};

export const isEligibleForMedBuyflow = checkSegments({
  oneOf: [
    isInAppMedUpgrade,
    isProductSwitchCheckout,
    isEligibleForClinicalBuyflow,
    isEligibleForStandaloneHRTBuyflow,
    isEligibleForHealthBuyflow,
  ],
});

export function isZepboundCashPay() {
  const { userData } = getStore().getState();
  return userData.indicatedZepboundVialPreference;
}

export function hasIndicatedZepboundVialPreference() {
  return getSelectedMedPlanType() === MED_SKU.BRANDED_ZEPBOUND_VIAL;
}

export function isZepboundFeatureEnabled() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === FEATURE_PARAM.ZEPBOUND_KWIKPEN;
}

export function isWegovyPillPaidTraffic() {
  const feature = new URLSearchParams(window.location.search).get("feature");
  return (
    feature === FEATURE_PARAM.WEGOVY_PILL ||
    feature === FEATURE_PARAM.WEGOVY_PILL_DEPRECATED
  );
}

export function isMicrodoseWegovyPillFeatureEnabled() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === FEATURE_PARAM.MICRODOSE_WEGOVY_PILL;
}

export function isWegovyPenFeatureEnabled() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === FEATURE_PARAM.WEGOVY_PEN;
}

export function isFoundayoFeatureEnabled() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === FEATURE_PARAM.FOUNDAYO;
}

/** `noom_entry` value that puts a user into the Medicare bridge experience. */
export const MEDICARE_LP_NOOM_ENTRY = "medicare_lp";

/** Medicare ads LP variant on /lp/clinical when noom_entry=medicare_lp. */
export function isMedicareLpActive() {
  const params = new URLSearchParams(window.location.search);
  return params.get("noom_entry") === MEDICARE_LP_NOOM_ENTRY;
}

export function isTirzepatidePaidTraffic() {
  const params = new URLSearchParams(window.location.search);
  return params.get("feature") === FEATURE_PARAM.TIRZEPATIDE;
}

export function isMicrodoseTirzepatidePaidTraffic() {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("feature") === FEATURE_PARAM.MICRODOSE_TIRZEPATIDE &&
    isMicrodoseTirzepatideFeatureEnabled()
  );
}

/** BRANDED_ZEPBOUND_VIAL `flowEligibilityCheck`: URL `feature` zepbound/tirzepatide or `glpMedPreference` tirzepatide. */
export function isBrandedZepboundVialFlowEnabled() {
  const { glpMedPreference } = getSurveyAnswers();
  return (
    isZepboundFeatureEnabled() ||
    isTirzepatidePaidTraffic() ||
    glpMedPreference?.[0] === "tirzepatide"
  );
}

/**
 * BRANDED_WEGOVY_PILL `flowEligibilityCheck`: URL `feature` wegovypill, or main-survey
 * `glpMedPreference` is wegovypill.
 */
export function isBrandedWegovyPillFlowEnabled() {
  const { glpMedPreference } = getSurveyAnswers();
  return isWegovyPillPaidTraffic() || glpMedPreference?.[0] === "wegovypill";
}

export function isTirzepatideFeatureEnabled() {
  return getTirzepatideRollout().shouldEnableFeature();
}

export function isMicrodoseTirzepatideFeatureEnabled() {
  return getMicrodoseTirzepatideRollout().shouldEnableFeature();
}

export function shouldUseOriginalClinicalLandingAssets() {
  return (
    isWegovyPillPaidTraffic() ||
    isMicrodoseFlowPaidTraffic() ||
    isZepboundFeatureEnabled() ||
    isOralsMedPaidTraffic()
  );
}

export function shouldUseUpdatedMedsLayout() {
  return (
    (isTirzepatidePaidTraffic() ||
      ((isCoreClinicalRoute() || isFreemiumClinicalRoute()) &&
        isTirzepatideFeatureEnabled())) &&
    !shouldUseOriginalClinicalLandingAssets()
  );
}

export const isEligibleForDiabetesSupport = (state = getStore().getState()) => {
  const diabetesDiagnosis = state?.surveyAnswers?.diabetesDiagnosis?.[0];
  const diabetesType = state?.surveyAnswers?.diabetesType?.[0];
  return (
    diabetesDiagnosis === "yes" &&
    (diabetesType === "type2" || diabetesType === "unsure")
  );
};

export function hasSignedLiabilityWaiver(state = getStore().getState()) {
  const surveyAnswers = getSurveyAnswers(state);
  return surveyAnswers.liabilityWaiver?.[0] === "agree";
}

export function isMicrodosePaidTraffic() {
  return getURLParams()?.feature === FEATURE_PARAM.MICRODOSE;
}

/**
 * Traffic on the microdose buyflow: `feature=microdose` or
 * `feature=microdoseTirzepatide`. Both params share identical flow routing;
 * they only differ in the default plan recommendation (MDS vs. MDS Plus).
 */
export function isMicrodoseFlowPaidTraffic() {
  return isMicrodosePaidTraffic() || isMicrodoseTirzepatidePaidTraffic();
}

export function isCompoundedFeature() {
  return getURLParams()?.feature === FEATURE_PARAM.COMPOUNDED;
}

export function isBrandedFeature() {
  return getURLParams()?.feature === FEATURE_PARAM.BRANDED;
}

export function isMicrodoseProactiveHealthFeature() {
  return getURLParams()?.feature === FEATURE_PARAM.MICRODOSE_PROACTIVE_HEALTH;
}

export function isTelex242BVariation() {
  return !!getMeristemConfigValue("isTelex242BVar");
}

export function checkPDQuestionsEligibility() {
  const {
    pdFatigue,
    pdLifting,
    pdGastrointestinalDiscomfort,
    pdGastrointestinalIssues,
    pdSideEffectConcern,
    pdNauseaImpact,
  } = getSurveyAnswers();

  if (
    ["low", "somewhatLow", "moderate"].includes(pdFatigue?.[0]) ||
    ["none", "01"].includes(pdLifting?.[0]) ||
    ["01_to_03", "04_to_09", "10+"].includes(
      pdGastrointestinalDiscomfort?.[0]
    ) ||
    (pdGastrointestinalIssues && !pdGastrointestinalIssues.includes("none")) ||
    ["somewhatConcerned", "veryConcerned"].includes(pdSideEffectConcern?.[0]) ||
    ["yes", "notSure"].includes(pdNauseaImpact?.[0])
  ) {
    return true;
  }
  return false;
}

export enum PDExceptionReason {
  AGE_50_PLUS = "AGE_50_PLUS",
  CONTINUITY_OF_CARE = "CONTINUITY_OF_CARE",
}

export function getPersonalizedDosingEligibility(): {
  isEligible: boolean;
  reason?: PDExceptionReason;
} {
  const questionEligibility = checkPDQuestionsEligibility();

  if (questionEligibility) {
    return { isEligible: true };
  }

  if (isAge50Plus()) {
    return {
      isEligible: true,
      reason: PDExceptionReason.AGE_50_PLUS,
    };
  }

  const { currentSubscriptionData } = getStore().getState();
  if (
    isContinuityOfCareEnabled() &&
    currentSubscriptionData.continuityOfCareEligible
  ) {
    return {
      isEligible: true,
      reason: PDExceptionReason.CONTINUITY_OF_CARE,
    };
  }

  return { isEligible: false };
}

export const isQualifiedForPersonalizedDosing = () => {
  return getPersonalizedDosingEligibility().isEligible;
};

export function isAge50Plus() {
  const { ageRange, ageYears } = getSurveyAnswers();
  const ageParsed = ageRange?.[0] ?? transformAgeYearsToAgeRange(ageYears);

  return ["50s", "60s", "70s"].includes(ageParsed);
}

/** True when ageRange is 60s/70s, or ageYears is between 60 and 79 inclusive. */
export function isAge60To79() {
  const { ageRange, ageYears } = getSurveyAnswers();
  if (ageYears != null && ageYears >= 60 && ageYears <= 79) {
    return true;
  }
  return ["60s", "70s"].includes(ageRange?.[0]);
}

export function hasWeightLossGoal(state = getStore().getState()) {
  const { bodyCompositionGoal } = getSurveyAnswers(state);
  return bodyCompositionGoal?.[0] === "loseWeight";
}

export function hasImproveBodyCompositionGoal(state = getStore().getState()) {
  const { bodyCompositionGoal } = getSurveyAnswers(state);
  return bodyCompositionGoal?.[0] === "improveBodyComposition";
}

export function hasWeightLossGoalBehavior(state = getStore().getState()) {
  const { healthGoalBehavior } = getSurveyAnswers(state);

  return (
    healthGoalBehavior != null &&
    Array.isArray(healthGoalBehavior) &&
    healthGoalBehavior.includes("loseWeight")
  );
}

export function isAffiliateTrial30PercentOff() {
  return (
    isAffiliateWithTrial() &&
    isUS() &&
    isEnLanguage() &&
    visitTracker.getTrackingParams().utm_content === "1974100"
  );
}

export function isAffiliateIntroBannerEligible() {
  return visitTracker.getTrackingParams()?.utm_content === "1894300";
}

export function getAffiliateIntroBannerFootnoteSymbol() {
  return "**";
}

export const shouldSendWeightLossGoalData = checkSegments({
  noneOf: [isAppFreeOnboardingRoute],
});

export const isCountryEligibleForAppFreeOnboarding = checkSegments({
  // Uses app country when available, and falls back to web country via getAppCountryCode().
  oneOf: [isAppUS, isAppCA, isAppAU, isAppGB],
});

export const isEligibleForAppFreeOnboarding = checkSegments({
  allOf: [
    isAppFreeOnboardingRoute,
    isEnLanguage,
    isCountryEligibleForAppFreeOnboarding,
  ],
});

export const isEligibleForAppFreeOnboardingProfile = checkSegments({
  allOf: [
    isAppFreeOnboardingProfileRoute,
    isEnLanguage,
    isCountryEligibleForAppFreeOnboarding,
  ],
});

export const isEligibleForFutureMe = () => {
  const { ageYears } = getSurveyAnswers();
  // const isAppVersionGTE13280 = isAppVersionGTE("13.28.0");
  const isAgeValid = !!ageYears && ageYears >= 18 && ageYears <= 70;
  // return isAppVersionGTE13280 && isAgeValid;
  return isAgeValid;
};

export const isFutureMeSupported = () =>
  getURLParams()?.is_future_me_supported === "true" &&
  !!appConfig.FUTURE_ME_ENABLED;

export const isFriendsPromotionSupported = () =>
  getURLParams()?.is_friends_supported === "true";

export const isFreeGlp1CompanionFlowEnabled = () =>
  getURLParams()?.glp1_o === "y";

export const isImportContactsSupported = () =>
  getURLParams()?.is_import_contacts_supported === "true";

export const isAddPhoneNumberSupported = () =>
  getURLParams()?.is_add_phone_number_supported === "true";

export const isAtRiskSubscriberReOnboarding = checkSegments({
  allOf: [
    isEnLanguage,
    () => getURLParams()?.utm_medium === "app",
    () => getURLParams()?.utm_campaign === "ex1120",
  ],
});

export function isLongevityMicrodoseEnabled() {
  return getLongevityMicrodoseRollout().shouldEnableFeature();
}

export function isCompoundedMicrodoseProactiveHealthBiomarkerEligible() {
  return getEligibleMedProducts().includes(
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
}

export function isCompoundedMicrodoseProactiveHealthBiomarkerEligibleBeforeEatingDisorderQuestion() {
  return getEligibleMedProducts({
    ...getDefaultEligibilityOptions(),
    skipEatingDisorderCheck: true,
  }).includes(MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER);
}

/**
 * Checks if user is on clinical route with idealWeight === currentWeight
 * and is eligible for Proactive Health Biomarker.
 */
export function isWeightMaintainClinicalEligible(): boolean {
  if (!isClinicalRoute()) return false;

  const { idealWeightKg, weightKg } = getSurveyAnswers();

  if (idealWeightKg === undefined || weightKg === undefined) return false;
  if (idealWeightKg !== weightKg) return false;

  return shouldShowProactiveHealthFlowAfterBMIQuestions();
}

/**
 * Determines if user should see Proactive Health phase 2 flow.
 */
export function shouldShowProactiveHealthFlow() {
  const eligibleProducts = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: true,
    checkState: true,
    checkFlow: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: [
      "isQualifiedForPD",
      "isNotEligibleForCompoundedSemaglutide",
    ],
  });

  return isEligibleForProactiveHealthOnly(eligibleProducts);
}

export function shouldShowProactiveHealthFlowAfterBMIQuestions() {
  const eligibleProducts = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: [
      "isQualifiedForPD",
      "isNotEligibleForCompoundedSemaglutide",
    ],
  });

  return isEligibleForProactiveHealthOnly(eligibleProducts);
}

export function shouldShowProactiveHealthFlowAfterHealthRiskQuestion() {
  const eligibleProducts = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: true,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: [
      "isQualifiedForPD",
      "isNotEligibleForCompoundedSemaglutide",
    ],
  });

  return isEligibleForProactiveHealthOnly(eligibleProducts);
}

export function isEligibleForProactiveHealthOnly(eligibleProducts: MED_SKU[]) {
  return (
    eligibleProducts.length === 1 &&
    eligibleProducts[0] ===
      MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
}

export function isEligibleForA1cLabsInterstitialBiomarkerVariant() {
  const glp1EligibleProducts = getEligibleGlp1Products();

  const hasOnlyProactiveHealth =
    glp1EligibleProducts.length === 1 &&
    glp1EligibleProducts[0] ===
      MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER;

  const hasHigherDoseGlp1 = isFulldoseGlp1Eligible(glp1EligibleProducts);

  // exclude users with improveBodyComposition goal who have higher dose eligibility
  const shouldExcludeBodyCompositionHigherDose =
    hasImproveBodyCompositionGoal() && hasHigherDoseGlp1;

  return (
    !shouldExcludeBodyCompositionHigherDose &&
    (hasOnlyProactiveHealth ||
      (glp1EligibleProducts.length >= 1 && !hasWeightLossGoal()))
  );
}

export function shouldSkipPreviousMedicationsQuestion() {
  const products = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: true,
    disabledNamedChecks: [
      "isQualifiedForPD",
      "isNotEligibleForCompoundedSemaglutide",
    ],
  });

  return (
    products.length === 1 &&
    products[0] === MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
}

export function isEligibleForCompoundedAndCompoundedMicrodose() {
  const eligibleProducts = getEligibleMedProducts();

  return (
    eligibleProducts.includes(MED_SKU.COMPOUNDED) &&
    eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE)
  );
}

/**
 * PageSet gate for `/payment/survey/medicationPreference`. Delegates to
 * `getResolvedMedicationPreferenceId` so the gate and the page-level resolver
 * share a single source of truth — if the resolver can't pick a screen, we
 * skip the page entirely instead of navigating to it and rendering an
 * `<ErrorPage />`.
 */
export function shouldShowMedicationPreference() {
  return getResolvedMedicationPreferenceId() !== undefined;
}

/**
 * Routing helper for the Wegovy pill vs. compounded microdose screen.
 * `feature=wegovypill` paid traffic gets the V2 redesign; all other paths
 * (organic, `glpMedPreference=wegovypill` answer without the URL param, etc.)
 * stay on the original V1 screen.
 */
function getWegovyPillMicrodosePreferenceId(): MedicationPreferenceId {
  if (isWegovyPillPaidTraffic()) {
    return MEDICATION_PREFERENCE_ID.BRANDED_WEGOVY_PILL_OR_COMPOUNDED_MICRODOSE_V2;
  }
  return MEDICATION_PREFERENCE_ID.BRANDED_WEGOVY_PILL_OR_COMPOUNDED_MICRODOSE;
}

type DosingAnswer = "microdose" | "regularDose" | "openToOptions";
type GlpAnswer = "semaglutide" | "tirzepatide" | "wegovypill" | "openToOptions";

/**
 * Lookup table for `getResolvedMedicationPreferenceId`. Drives the
 * `dosingPreference` × `glpMedPreference` routing for the microdose
 * paid-traffic funnel. The first rule whose `(dosing, glp)` matches the
 * user's survey answers AND whose `requiredSkus` are all eligible wins.
 *
 * `preferenceId` is either:
 *  - a `MedicationPreferenceId` — used directly.
 *  - a `() => MedicationPreferenceId` resolver — for cases like Wegovy pill
 *    V1 vs. V2 that branch on runtime state.
 *  - `undefined` — the screen for that combination doesn't exist yet.
 *    Rules with `undefined` are treated as no-ops so routing falls through
 *    to the legacy GLP / feature-param branches below.
 */
type MicrodoseFlowPreferenceRule = {
  dosing: DosingAnswer;
  glp: GlpAnswer;
  requiredSkus: MED_SKU[];
  preferenceId:
    | MedicationPreferenceId
    | (() => MedicationPreferenceId)
    | undefined;
};

const MICRODOSE_FLOW_PREFERENCE_RULES: MicrodoseFlowPreferenceRule[] = [
  {
    dosing: "microdose",
    glp: "semaglutide",
    requiredSkus: [MED_SKU.COMPOUNDED_MICRODOSE, MED_SKU.BRANDED],
    preferenceId: MEDICATION_PREFERENCE_ID.COMPOUNDED_MICRODOSE_OR_BRANDED,
  },
  {
    dosing: "microdose",
    glp: "tirzepatide",
    requiredSkus: [
      MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
      MED_SKU.BRANDED_ZEPBOUND_VIAL,
    ],
    preferenceId:
      MEDICATION_PREFERENCE_ID.COMPOUNDED_MICRODOSE_TIRZEPATIDE_OR_BRANDED_ZEPBOUND_VIAL,
  },
  {
    dosing: "microdose",
    glp: "openToOptions",
    requiredSkus: [
      MED_SKU.COMPOUNDED_MICRODOSE,
      MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
    ],
    preferenceId:
      MEDICATION_PREFERENCE_ID.COMPOUNDED_MICRODOSE_OR_COMPOUNDED_MICRODOSE_TIRZEPATIDE,
  },
  {
    dosing: "regularDose",
    glp: "semaglutide",
    requiredSkus: [MED_SKU.COMPOUNDED, MED_SKU.BRANDED],
    preferenceId: MEDICATION_PREFERENCE_ID.COMPOUNDED_OR_BRANDED,
  },
  {
    dosing: "regularDose",
    glp: "tirzepatide",
    requiredSkus: [
      MED_SKU.COMPOUNDED_TIRZEPATIDE,
      MED_SKU.BRANDED_ZEPBOUND_VIAL,
    ],
    preferenceId:
      MEDICATION_PREFERENCE_ID.COMPOUNDED_TIRZEPATIDE_OR_BRANDED_ZEPBOUND_VIAL,
  },
  {
    dosing: "regularDose",
    glp: "wegovypill",
    requiredSkus: [MED_SKU.BRANDED_WEGOVY_PILL, MED_SKU.COMPOUNDED_MICRODOSE],
    preferenceId: getWegovyPillMicrodosePreferenceId,
  },
  {
    dosing: "regularDose",
    glp: "openToOptions",
    requiredSkus: [MED_SKU.COMPOUNDED, MED_SKU.COMPOUNDED_TIRZEPATIDE],
    preferenceId: MEDICATION_PREFERENCE_ID.COMPOUNDED_TIRZEPATIDE_OR_COMPOUNDED,
  },
  {
    dosing: "openToOptions",
    glp: "semaglutide",
    requiredSkus: [MED_SKU.COMPOUNDED_MICRODOSE, MED_SKU.BRANDED],
    preferenceId: MEDICATION_PREFERENCE_ID.COMPOUNDED_MICRODOSE_OR_BRANDED,
  },
  {
    dosing: "openToOptions",
    glp: "tirzepatide",
    requiredSkus: [
      MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
      MED_SKU.BRANDED_ZEPBOUND_VIAL,
    ],
    preferenceId:
      MEDICATION_PREFERENCE_ID.COMPOUNDED_MICRODOSE_TIRZEPATIDE_OR_BRANDED_ZEPBOUND_VIAL,
  },
  {
    dosing: "openToOptions",
    glp: "wegovypill",
    requiredSkus: [MED_SKU.BRANDED_WEGOVY_PILL, MED_SKU.COMPOUNDED_MICRODOSE],
    preferenceId: getWegovyPillMicrodosePreferenceId,
  },
  {
    dosing: "openToOptions",
    glp: "openToOptions",
    requiredSkus: [
      MED_SKU.COMPOUNDED_MICRODOSE,
      MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
    ],
    preferenceId:
      MEDICATION_PREFERENCE_ID.COMPOUNDED_MICRODOSE_OR_COMPOUNDED_MICRODOSE_TIRZEPATIDE,
  },
];

function resolveMicrodoseFlowPreferenceId(
  dosingAnswer: string | undefined,
  glpAnswer: string | undefined
): MedicationPreferenceId | undefined {
  for (const rule of MICRODOSE_FLOW_PREFERENCE_RULES) {
    if (
      rule.dosing === dosingAnswer &&
      rule.glp === glpAnswer &&
      rule.requiredSkus.every(isEligibleForMedSku)
    ) {
      const id =
        typeof rule.preferenceId === "function"
          ? rule.preferenceId()
          : rule.preferenceId;
      if (id !== undefined) return id;
    }
  }
  return undefined;
}

/**
 * Dev-mode override for `getResolvedMedicationPreferenceId`. When
 * `?idm=true&medicationPreferenceOverride=<ID>` is present and the value
 * matches a known `MedicationPreferenceId`, the resolver short-circuits
 * to the override — bypassing survey-answer, eligibility, and
 * feature-param routing. Used by QA/dev to preview any medication
 * preference screen without navigating the full flow.
 *
 * Silently returns `undefined` (letting the normal resolver run) if
 * `idm=true` isn't set, no override is provided, or the value isn't a
 * known preference id.
 */
function getMedicationPreferenceOverride(): MedicationPreferenceId | undefined {
  if (!isDevMode()) return undefined;
  const override = getURLParams()?.medicationPreferenceOverride;
  if (!override) return undefined;
  const validIds = Object.values(MEDICATION_PREFERENCE_ID) as string[];
  if (!validIds.includes(override)) return undefined;
  return override as MedicationPreferenceId;
}

/**
 * Which medication preference screen applies on `/payment/survey/medicationPreference`
 * (`glpMedPreference` first, then URL `feature`). Resolves `MEDICATION_PREFERENCE_ID` for
 * the matching `MedicationPreferenceConfig` in `medicationPreference.tsx`.
 */
export function getResolvedMedicationPreferenceId():
  | MedicationPreferenceId
  | undefined {
  const override = getMedicationPreferenceOverride();
  if (override !== undefined) return override;

  const glpAnswer = getSurveyAnswers().glpMedPreference?.[0];
  const dosingAnswer = getSurveyAnswers().dosingPreference?.[0];

  if (isMicrodoseTirzepatideFeatureEnabled()) {
    // Microdose paid-traffic routing: when the user has answered both
    // `dosingPreference` and `glpMedPreference`, consult the lookup table for
    // the dosing × GLP combination. Falls through to the GLP-answer / feature
    // branches below when the rule isn't ready yet (`preferenceId: undefined`),
    // eligibility SKUs are missing, or the user hasn't answered both questions.
    if (isMicrodoseFlowPaidTraffic() && dosingAnswer && glpAnswer) {
      const microdoseFlowId = resolveMicrodoseFlowPreferenceId(
        dosingAnswer,
        glpAnswer
      );
      if (microdoseFlowId !== undefined) return microdoseFlowId;
    }
  } else if (
    (dosingAnswer === "microdose" || dosingAnswer === "openToOptions") &&
    isEligibleForMedSku(MED_SKU.COMPOUNDED) &&
    isEligibleForMedSku(MED_SKU.COMPOUNDED_MICRODOSE)
  ) {
    return MEDICATION_PREFERENCE_ID.COMPOUNDED_OR_COMPOUNDED_MICRODOSE;
  }

  // Non Microdose / DosingPreference Logic
  if (
    glpAnswer === "tirzepatide" &&
    isEligibleForMedSku(MED_SKU.COMPOUNDED_TIRZEPATIDE) &&
    isEligibleForMedSku(MED_SKU.BRANDED_ZEPBOUND_VIAL)
  ) {
    return MEDICATION_PREFERENCE_ID.COMPOUNDED_TIRZEPATIDE_OR_BRANDED_ZEPBOUND_VIAL;
  }

  if (
    glpAnswer === "semaglutide" &&
    isEligibleForMedSku(MED_SKU.COMPOUNDED) &&
    isEligibleForMedSku(MED_SKU.BRANDED)
  ) {
    return MEDICATION_PREFERENCE_ID.COMPOUNDED_OR_BRANDED;
  }

  if (
    glpAnswer === "wegovypill" &&
    isEligibleForMedSku(MED_SKU.BRANDED_WEGOVY_PILL) &&
    isEligibleForMedSku(MED_SKU.COMPOUNDED_MICRODOSE)
  ) {
    return getWegovyPillMicrodosePreferenceId();
  }

  if (!glpAnswer || glpAnswer === "openToOptions") {
    if (
      isNoPaidTraffic() &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED_TIRZEPATIDE) &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED)
    ) {
      return MEDICATION_PREFERENCE_ID.COMPOUNDED_TIRZEPATIDE_OR_COMPOUNDED;
    }

    if (
      isWegovyPillPaidTraffic() &&
      isEligibleForMedSku(MED_SKU.BRANDED_WEGOVY_PILL) &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED_MICRODOSE)
    ) {
      return getWegovyPillMicrodosePreferenceId();
    }

    if (
      isTirzepatidePaidTraffic() &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED_TIRZEPATIDE) &&
      isEligibleForMedSku(MED_SKU.BRANDED_ZEPBOUND_VIAL)
    ) {
      return MEDICATION_PREFERENCE_ID.COMPOUNDED_TIRZEPATIDE_OR_BRANDED_ZEPBOUND_VIAL;
    }
    // These two returns below will get removed after we merge, this is a remnant of the old dosingPreference logic
    // Now that we are allowing glpMedPreference to be shown when dosingPreference is regularDose,
    // These cases will be handled in the for loop checking the mappings above after cleanup.
    if (
      !isMicrodoseTirzepatideFeatureEnabled() &&
      isMicrodosePaidTraffic() &&
      dosingAnswer === "regularDose" &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED_TIRZEPATIDE) &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED)
    ) {
      return MEDICATION_PREFERENCE_ID.COMPOUNDED_TIRZEPATIDE_OR_COMPOUNDED;
    }

    if (
      !isMicrodoseTirzepatideFeatureEnabled() &&
      isMicrodosePaidTraffic() &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED) &&
      isEligibleForMedSku(MED_SKU.COMPOUNDED_MICRODOSE)
    ) {
      return MEDICATION_PREFERENCE_ID.COMPOUNDED_OR_COMPOUNDED_MICRODOSE;
    }
  }

  return undefined;
}

export const isOpenToMedRecommendation = () => {
  const {
    medicationPreference,
    oralsMedicationPreference,
    oralsPlanPreference,
  } = getSurveyAnswers();

  return Boolean(
    !shouldRecommendZepbound() &&
      oralsMedicationPreference?.[0] !== "no" &&
      (!medicationPreference || medicationPreference === "RECOMMENDATION") &&
      (!oralsPlanPreference || oralsPlanPreference === "RECOMMENDATION")
  );
};

export function shouldUseOffersRecommendation() {
  return getMeristemConfigValue("shouldUseOffersRecommendation") === "true";
}

export function getForcedMedRecommendationSku() {
  const value = getMeristemConfigValue("forcedMedRecommendation");
  return Object.values(MED_SKU).includes(value as MED_SKU)
    ? (value as MED_SKU)
    : null;
}

export const shouldCallRecommenderOffersApi = () => {
  return isClinicalRoute();
};

export const shouldRecommendMDS1MonthAsDefault = () => {
  return getPlandisplayBilling()?.billing !== "112";
};

export const isBiomarkerMicrodosePlanSelected = () => {
  return (
    getSelectedMedPlanType() ===
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
};

export const shouldShowNoTrialMedCheckout = () => {
  return (
    isBiomarkerMicrodosePlanSelected() ||
    (hasSelectedMicrodosePlan() &&
      (isMicrodoseNoTrialEnabled() ||
        (isMicrodoseMonthlyEnabled() && !isCalorieClinicalRoute())))
  );
};

export const isTelex265BActive = (state = getStore().getState()) => {
  // Pseudobaseline Telex265B for freemium-upsell-branched route.
  // Proper baseline will happen post-peak https://noomhq.atlassian.net/browse/UPSELL-1540.
  // The baseline requires that the isTelex265BActive be set to true when the user starts off
  // in the freemium-upsell-branched route. If they proceed to the clinical upsell route, we
  // still want to set the isTelex265BActive to true. Use the session storage to keep track of this.
  const sessionStorageValue = getNoomSessionStorage<boolean>(
    "isTelex265BPseudoBaselineActive"
  );
  if (sessionStorageValue === true) {
    return true;
  }

  // If coming from freemium branched route, store in session storage and return true
  if (isFreemiumBranchedRoute(state)) {
    setNoomSessionStorage("isTelex265BPseudoBaselineActive", true);
    return true;
  }

  // Otherwise, fall back to meristem config value
  return !!getMeristemConfigValue("isTelex265BActive");
};

export const showSeedsEducationScreen = () => {
  return isAppFreeOnboardingScreenVisible("seedsEducation");
};

export const showRealWorldRewardsScreen = () => {
  return isAppFreeOnboardingScreenVisible("realWorldRewards");
};

export const showFaceScanIntroScreen = () => {
  return isAppFreeOnboardingScreenVisible("faceScanIntro");
};

export const showFacialBloodScanScreen = () => {
  return isAppFreeOnboardingScreenVisible("facialBloodScan");
};

function isAppFreeOnboardingScreenVisible(pageName: string): boolean {
  const urlParams = getURLParams();
  const visibilityConfig = urlParams?.v_conf?.split(",") ?? [];
  return visibilityConfig.includes(pageName);
}

// Flows that use the Orders API for managing subscriptions
export const isOrdersCheckout = checkSegments({
  oneOf: [
    isB2BUpsellClinicalRoute,
    isOnAppleExternalLinkoutPath,
    isCrossSellShopCheckout,
    isProductSwitchCheckout,
    isInAppMedUpgrade,
  ],
});

// Flows that use Orders API billing-cycle previews: full Orders checkout
// (`isOrdersCheckout`) and legacy purchase that still previews tax via Orders.
// Preview-only call sites (createOrder / updateBillingUser) still use
// `isOrdersPreviewPurchaseCheckout() && !isOrdersCheckout()`.
export const isOrdersPreviewPurchaseCheckout = checkSegments({
  oneOf: [
    isOrdersCheckout,
    isEligibleForMedBuyflow,
    isApplePurchaseLinkoutEligible,
  ],
});

export const show72MoreWeightDisclaimer = () => {
  const urlParams = getURLParams();
  return urlParams?.utm_content === "moreweightloss_popup";
};

export const shouldHidePartnerAd = () => {
  if (isCalorieClinicalRoute() && isInsidePartnerWebView()) {
    return true;
  }

  return false;
};

export function isContinuityOfCareEnabled() {
  return (
    getMedFlagOverride("continuityOfCareEnabledOverwrite") ??
    !!appConfig.CONTINUITY_OF_CARE_ENABLED
  );
}

export const getMedPlanChangeTermsExpVariant = () =>
  getMeristemConfigValue("autoRenewalTermsPosition") as "checkbox" | null;

export const shouldUseStickyProgressBar = () => {
  return (
    getMeristemConfigValue("isStickyProgressBarExperimentEnabled") === "true"
  );
};

export function isTelex274Active() {
  return !!getMeristemConfigValue("isTelex274Active");
}

/**
 * Returns true if the user has the medication companion feature enabled
 * based on baselined.features.medicationCompanion.enabled in the user model.
 */
export const isMedicationCompanionUser = (): boolean => {
  const { medEntitlements } = getStore().getState();
  return !!medEntitlements.hasMedCompanion;
};

/**
 * Determines whether the meal logging interstitial should be shown
 * based on UTM source parameter from the current URL
 */
export function shouldShowMealLoggingInterstitial(): boolean {
  const urlParams = getURLParams();
  const utmSource = urlParams?.utm_source;
  return mealLoggingInterstitialConfig.ALLOWED_UTM_SOURCES.includes(
    utmSource || ""
  );
}

export function isTelex276Active() {
  return !!getMeristemConfigValue("isTelex276Active");
}

export const isOnboardingHealthReport = () =>
  getURLParams()?.ad_location === UpsellAdLocation.OnboardingHealthReport;

export function isQualifiedForFullDoseGLP1ForHealth() {
  const products = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: true,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    disabledNamedChecks: ["isQualifiedForPD"],
  });

  return isFulldoseGlp1Eligible(products);
}

export function isQualifiedForFullDoseGLP1ForHealthBeforeEatingDisorderQuestion() {
  const products = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: true,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    disabledNamedChecks: ["isQualifiedForPD"],
    skipEatingDisorderCheck: true,
  });

  return isFulldoseGlp1Eligible(products);
}

export function isEligibleForWeightGraphInMedCheckout() {
  if (isProductSwitchCheckout() || isInAppMedUpgrade()) {
    return false;
  }
  if (acceptedNoomClinicalOffer() && hasSelectedMicrodoseBiomarkerPlan()) {
    return false;
  }

  if (isHealthRoute() && !hasWeightLossGoal()) {
    return false;
  }

  // Fallback in case somehow a user doesn't have an ideal weight
  if (!hasIdealWeight()) {
    return false;
  }

  return true;
}

export const hasHealthReportUpsellContent = (
  { upsellAdData } = getStore().getState()
) => !!(upsellAdData?.healthReportUpsell?.content as HealthReportUpsellContent);

export function isOnAppleExternalLinkoutPath() {
  return window.location.pathname.startsWith("/payment/linkout/");
}

export const shouldUseMinimumMedBMIForHealthRoute = () => {
  const products = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    disabledNamedChecks: ["isQualifiedForPD"],
  });
  return isHealthRoute() && products.length > 0;
};

export function hasAtLeast1Glp1EligibleBeforeEatingDisorderQuestion() {
  return getEligibleGlp1Products({ skipEatingDisorderCheck: true }).length >= 1;
}

export function shouldShowKickstartChangeForHealthRoute() {
  const eligibleProducts = getEligibleMedProducts({
    checkBMI: true,
    checkHealthRisks: false,
    checkState: false,
    checkFlow: true,
    requirePreviousMedicationsAnswer: false,
    requireHighestBMIAnswer: false,
    disabledNamedChecks: ["isQualifiedForPD"],
    skipEatingDisorderCheck: true,
  });

  return (
    isFulldoseGlp1Eligible(eligibleProducts) ||
    (eligibleProducts.length === 1 &&
      eligibleProducts[0] ===
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER)
  );
}

export function isTelex280Active() {
  return !!getMeristemConfigValue("isTelex280Active");
}

/**
 * Telex314: two-step insurance checker. When active, the first insurance check
 * runs demographics-only and, if it does not find a match, the user enters card
 * details on `insuranceInfo` before a second card-based check
 * (`insuranceCheckerRetryLoading`). When inactive, the original single-check
 * flow runs (`insuranceInfo` is collected before the one check).
 */
export function isTelex314Active() {
  return !!getMeristemConfigValue("isTelex314Active");
}

export function isTelex289Active() {
  return !!getMeristemConfigValue("isTelex289Active");
}

function isTelex311Active() {
  return !!getMeristemConfigValue("isTelex311Active");
}

/**
 * Gates the 24- and 48-week med checkout plans, offered either through
 * Telex311 or to traffic tagged with their own plandisplay code.
 */
export function isLongDurationPlansEnabled() {
  const { plandisplay } = getPlandisplayBilling();

  return isTelex311Active() || plandisplay === "KSKA8O";
}

export function shouldHideGLPMedPreferenceQuestion() {
  if (isMicrodoseTirzepatideFeatureEnabled()) {
    return false;
  }

  const { dosingPreference } = getSurveyAnswers();
  return (
    dosingPreference?.[0] === "microdose" ||
    dosingPreference?.[0] === "openToOptions"
  );
}

export function isMicrodoseDosingPreference() {
  return getSurveyAnswers().dosingPreference?.[0] === "microdose";
}

export function isHCEx1Active() {
  return !!getMeristemConfigValue("isHCEx1Active");
}

/**
 * Gates the Medicare bridge interstitial (`/payment/survey/likelyEligibleForBridge`)
 * as well as the Medicare-priced copy/pill/disclaimer variants on the branded,
 * Zepbound vial, and Wegovy pill plan-selection and plan-reveal cards.
 * Requires Medicare bridge and branded-async eligibility.
 */
export function shouldShowLikelyEligibleForBridge() {
  return isEligibleForMedicareBridge() && isEligibleForBrandedAsync();
}

export function isTelex313LpActive() {
  return !!getMeristemConfigValue("isTelex313LpActive");
}

export function isTelex310cActive() {
  return !!getMeristemConfigValue("isTelex310cActive");
}

/**
 * Telex310C billing-display UI: Meristem flag (clinical route) plus an eligible
 * med SKU (branded, compounded, MDS, compounded tirzepatide, MDS tirzepatide).
 * Host filtering (in-app upgrade / product switch / B2B) is handled by Meristem
 * scoping isTelex310cActive to route=clinical.
 */
export function shouldShowTelex310cUI() {
  const telex310cEligibleSkus: MED_SKU[] = [
    MED_SKU.BRANDED,
    MED_SKU.COMPOUNDED,
    MED_SKU.COMPOUNDED_MICRODOSE,
    MED_SKU.COMPOUNDED_TIRZEPATIDE,
    MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
  ];

  return (
    isTelex310cActive() &&
    telex310cEligibleSkus.includes(getSelectedMedPlanType())
  );
}

/**
 * Whether growth should refuse this purchase when the user already has an
 * active shop subscription (MGEXP-3936).
 *
 * A purchase made outside an authenticated context silently repoints the
 * account's payment method and shipping address onto every active
 * subscription. Checkouts that expect signed-in users disclose that with the
 * active-subscription impact banner; checkouts that only ever expect
 * logged-out users cannot show it, so the purchase is blocked there instead
 * and the user is directed to buy from the app.
 */
export function shouldBlockPurchaseOnActiveSubscription() {
  return shouldEnrollUser() && !shouldShowActiveSubscriptionImpactBanner();
}

export function shouldUsePostAuthCheckoutFlow() {
  return isPostAuthCheckoutSession();
}
/**
 * Determines if imperial units (feet and pounds) should be used.
 *
 * Priority order for country code selection:
 * 1. If `country_code` is forced (e.g., user-selected), always use `getCountryCode()`
 * 2. If user is NOT in-app, use `getCountryCode()`
 * 3. If user is in-app, use `getAppCountryCode()` (prefers `app_country_code`,
 *    falls back to `country_code` if `app_country_code` is not available)
 */
export const shouldUseImperialUnits = () => {
  const { is_forced_country_code } = getMeristemContext() as any;
  const countryCode: CountryCode =
    is_forced_country_code || !isInApp()
      ? getCountryCode()
      : getAppCountryCode();
  return ["US", "CA"].includes(countryCode);
};

/**
 * Determines if British units logic should be used.
 *
 * Uses the same country code precedence as `shouldUseImperialUnits()`:
 * - forced `country_code` wins
 * - otherwise, in-app prefers `app_country_code`
 */
export const shouldUseBritishUnits = () => {
  const { is_forced_country_code } = getMeristemContext() as any;
  const countryCode: CountryCode =
    is_forced_country_code || !isInApp()
      ? getCountryCode()
      : getAppCountryCode();
  return ["UK", "GB", "IE"].includes(countryCode);
};

export function isProductSwitchEnabled(): boolean {
  return (
    getMedFlagOverride("productSwitchEnabledOverwrite") ??
    !!appConfig.PRODUCT_SWITCH_ENABLED
  );
}

/**
 * Gates the novo buyflow soft-launch experience.
 *
 * Enabled when the grow-config flag is on and the user's IP subdivision is in
 * `NOVO_BUYFLOW_ROLLOUT_STATES`. Force-enable in dev with
 * `?idm=true&novoBuyflowRolloutEnabled=true` (or force-disable with `=false`).
 */
export function isNovoBuyflowEnabled(): boolean {
  const isNovoSoftLaunchRoute =
    isCoreClinicalRoute() || isInsuranceCheckerRoute();
  const isInRollout =
    getMedFlagOverride("novoBuyflowRolloutEnabled") ??
    (!!appConfig.NOVO_BUYFLOW_ENABLED &&
      appConfig.NOVO_BUYFLOW_ROLLOUT_STATES.includes(getSubdivision()));

  return isInRollout && isNovoSoftLaunchRoute;
}

/** True when `?feature=` is one of the med facades (Wegovy pen/pill, Foundayo, etc.). */
export function isMedMembershipFacadeFeature() {
  return !!getFacadeFromFeatureParam(getURLParams()?.feature);
}

export function getMedMembershipFacade(): MED_MEMBERSHIP_FACADE | undefined {
  if (!isNovoBuyflowEnabled()) {
    return undefined;
  }

  return resolveMedMembershipFacade(getSurveyAnswers());
}

export const isProductSwitchEligible = (): boolean => {
  const { productSwitch } = getStore().getState();
  return productSwitch?.offers?.length > 0;
};

export function isUpsEx365BrandedAsyncCrossSellActive() {
  return !!getMeristemConfigValue("isUpsEx365BrandedAsyncCrossSellActive");
}

export function isUpsEx366NadBeforeMetabolicActive() {
  return !!getMeristemConfigValue("isUpsEx366NadBeforeMetabolicActive");
}

export function isClinicalVendorEligibilityGateActive() {
  // Worker-controlled GrowConfig kill switch (not a Meristem experiment): the
  // gate must apply to 100% of traffic, and meristemConfig only reaches
  // journey-enrolled users.
  return appConfig.CLINICAL_VENDOR_ELIGIBILITY_GATE_ENABLED;
}

// Shop launch flags are worker-controlled GrowConfig kill switches (not Meristem
// experiments): logged-out shop traffic is not journey-enrolled. Med, OTC and
// oxytocin each have their own pair so they can go live on different dates.

export function isShopMedPdpRedesignEnabled() {
  return (
    getMedFlagOverride("shopMedPdpRedesignEnabledOverwrite") ??
    !!appConfig.SHOP_MED_PDP_REDESIGN_ENABLED
  );
}

export function isShopMedLoggedOutAccessEnabled() {
  return (
    getMedFlagOverride("shopMedLoggedOutAccessEnabledOverwrite") ??
    !!appConfig.SHOP_MED_LOGGED_OUT_ACCESS_ENABLED
  );
}

export function isShopOtcPdpRedesignEnabled() {
  return (
    getMedFlagOverride("shopOtcPdpRedesignEnabledOverwrite") ??
    !!appConfig.SHOP_OTC_PDP_REDESIGN_ENABLED
  );
}

export function isShopOtcLoggedOutAccessEnabled() {
  return (
    getMedFlagOverride("shopOtcLoggedOutAccessEnabledOverwrite") ??
    !!appConfig.SHOP_OTC_LOGGED_OUT_ACCESS_ENABLED
  );
}

export function isShopOxytocinPdpRedesignEnabled() {
  return (
    getMedFlagOverride("shopOxytocinPdpRedesignEnabledOverwrite") ??
    !!appConfig.SHOP_OXYTOCIN_PDP_REDESIGN_ENABLED
  );
}

export function isShopOxytocinLoggedOutAccessEnabled() {
  return (
    getMedFlagOverride("shopOxytocinLoggedOutAccessEnabledOverwrite") ??
    !!appConfig.SHOP_OXYTOCIN_LOGGED_OUT_ACCESS_ENABLED
  );
}

/**
 * Whether a hive-auth user is signed in. Not `isUserAuthenticated()`, which is
 * also true for a legacy auth cookie that may have failed to sign in.
 */
export function isUserLoggedIn() {
  return !!getActiveUser()?.accessCode;
}

/**
 * Whether the clinical-vendor eligibility gate applies: flag on, in an
 * authenticated med-purchase flow (freemium upsell clinical route or the
 * in-app NW→Med upgrade pageset).
 */
export function shouldApplyVendorEngineGate() {
  return (
    isClinicalVendorEligibilityGateActive() &&
    (isUpsellClinicalRoute() || isInAppMedUpgrade()) &&
    !!getActiveUser()?.accessCode
  );
}

const OTC_ELIGIBLE_MED_SKUS = [
  MED_SKU.COMPOUNDED,
  MED_SKU.GENERIC_LIRAGLUTIDE,
  MED_SKU.COMPOUNDED_MICRODOSE,
  MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
  MED_SKU.COMPOUNDED_TIRZEPATIDE,
];

const SERMORELIN_CROSS_SELL_ELIGIBLE_MED_SKUS = [
  ...OTC_ELIGIBLE_MED_SKUS,
  MED_SKU.ORALS_METFORMIN,
];

export function isUpsEx365BrandedAsyncCrossSellEligible(
  state = getStore().getState()
) {
  const planId =
    state.checkoutOrders?.saleItem?.legacyNoomPlanId ??
    state.recommendedPlan?.noom_plan_id;

  return (
    isUpsEx365BrandedAsyncCrossSellActive() && isBrandedAsyncNoomPlanId(planId)
  );
}

function isPostSignupCrossSellMedSkuEligible(eligibleSkus: MED_SKU[]) {
  return (
    eligibleSkus.includes(getSelectedMedPlanType()) ||
    // Branded async shares MED_SKU.BRANDED with sync, so it cannot go on the
    // allowlist. Ex365 uses the plan-id check instead.
    isUpsEx365BrandedAsyncCrossSellEligible()
  );
}

function hasMuscleInterestForSermorelinCrossSell() {
  const answer = getSurveyAnswers().weightLossGoalMed?.[0];
  return answer === "buildStrength" || answer === "maintainMuscle";
}

export function isEligibleForSermorelinCrossSellMed() {
  return (
    isUserAuthenticated() &&
    isUS() &&
    isEnLanguage() &&
    isCoreClinicalRoute() &&
    userHasPurchasedNoomClinical() &&
    isPostSignupCrossSellMedSkuEligible(
      SERMORELIN_CROSS_SELL_ELIGIBLE_MED_SKUS
    ) &&
    hasMuscleInterestForSermorelinCrossSell()
  );
}

export function shouldShowSermorelinCrossSellMed() {
  return (
    isEligibleForSermorelinCrossSellMed() &&
    isProductEligible(PEPTIDE_SERMORELIN_PRODUCT_NAME)
  );
}

const NAD_CROSS_SELL_FATIGUE_ANSWERS: readonly PdFatigueAnswer[] = [
  "low",
  "somewhatLow",
];

function hasLowEnergyForNadCrossSell() {
  const answer = getSurveyAnswers().pdFatigue?.[0];
  return !!answer && NAD_CROSS_SELL_FATIGUE_ANSWERS.includes(answer);
}

export function isEligibleForNadCrossSellMed() {
  return (
    isUserAuthenticated() &&
    isCoreClinicalRoute() &&
    !!userHasPurchasedNoomClinical() &&
    hasLowEnergyForNadCrossSell()
  );
}

export function shouldShowNadCrossSellMed() {
  return (
    isEligibleForNadCrossSellMed() &&
    (isUpsEx366NadBeforeMetabolicActive() ||
      isUpsEx365BrandedAsyncCrossSellEligible()) &&
    isProductEligible(RX_NAD_PLUS_PRODUCT_NAME)
  );
}

const METABOLIC_CROSS_SELL_ELIGIBLE_MED_SKUS = [
  ...OTC_ELIGIBLE_MED_SKUS,
  MED_SKU.ORALS_METFORMIN,
  MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
];

export function isEligibleForBiomarkersCrossSellMed() {
  return (
    isUserAuthenticated() &&
    isUS() &&
    isEnLanguage() &&
    userHasPurchasedNoomClinical() &&
    isPostSignupCrossSellMedSkuEligible(OTC_ELIGIBLE_MED_SKUS) &&
    // Ex365 branded-async users are clinical-route-only today, so the route
    // check already covers them; the extra clause keeps them eligible if the
    // experiment's targeting changes.
    (isCoreClinicalRoute() || isUpsEx365BrandedAsyncCrossSellEligible())
  );
}

const isRouteEligibleForWeightCrossSell = checkSegments({
  oneOf: [
    isCore,
    isEmailMain,
    isEmailWinback,
    isAffiliateWithTrial,
    isAppAndroidBase,
    isWinback,
    isReferralsRoute,
  ],
});

export function isStateEligibleBiomarkersCrossSellWeight(
  state = getStore().getState()
) {
  const { checkout } = state;
  const { zipcode } = checkout?.billingAddress ?? {};
  if (zipcode !== undefined) {
    const userState = zipState(zipcode);
    if (userState == null) return false;
    return !appConfig.OTC_BIOMARKERS_INELIGIBLE_US_STATES.includes(userState);
  }
  return false;
}

export function isEligibleForBiomarkersCrossSellWeight() {
  return (
    isUserAuthenticated() &&
    isUS() &&
    isEnLanguage() &&
    isRouteEligibleForWeightCrossSell() &&
    isStateEligibleBiomarkersCrossSellWeight()
  );
}

function isEligibleForMetabolicGenderTargetedSub(
  target: sexConstants.male | sexConstants.female,
  state = getStore().getState()
) {
  const { sex, gender } = getSurveyAnswers(state);
  const matchingGender =
    target === sexConstants.male
      ? genderConstants.male
      : genderConstants.female;
  return sex?.[0] === target && gender?.[0] === matchingGender;
}

export function isEligibleForMetabolicMenSub(state = getStore().getState()) {
  return isEligibleForMetabolicGenderTargetedSub(sexConstants.male, state);
}

export function isEligibleForMetabolicWomenSub(state = getStore().getState()) {
  return isEligibleForMetabolicGenderTargetedSub(sexConstants.female, state);
}

function isMetabolicMedCrossSellBaseEligible() {
  return (
    isUserAuthenticated() &&
    isUS() &&
    isEnLanguage() &&
    userHasPurchasedNoomClinical() &&
    isPostSignupCrossSellMedSkuEligible(
      METABOLIC_CROSS_SELL_ELIGIBLE_MED_SKUS
    ) &&
    // Ex365 branded-async users are clinical-route-only today, so the route
    // check already covers them; the extra clause keeps them eligible if the
    // experiment's targeting changes.
    (isCoreClinicalRoute() || isUpsEx365BrandedAsyncCrossSellEligible())
  );
}

export function isEligibleForMetabolicForMenCrossSellMed() {
  return (
    isMetabolicMedCrossSellBaseEligible() && isEligibleForMetabolicMenSub()
  );
}

function isStateEligibleForMetabolicWeightCrossSell(
  sku: ADDON_SKU.OTC_METABOLIC_MEN | ADDON_SKU.OTC_METABOLIC_WOMEN,
  state = getStore().getState()
) {
  const { checkout } = state;
  const { zipcode } = checkout?.billingAddress ?? {};
  if (zipcode !== undefined) {
    const userState = zipState(zipcode);
    if (userState == null) return false;
    return isStateEligibleForAddon(sku, userState);
  }
  return false;
}

export function isEligibleForMetabolicForMenCrossSellWeight() {
  return (
    isUserAuthenticated() &&
    isUS() &&
    isEnLanguage() &&
    isRouteEligibleForWeightCrossSell() &&
    isStateEligibleForMetabolicWeightCrossSell(ADDON_SKU.OTC_METABOLIC_MEN) &&
    isEligibleForMetabolicMenSub()
  );
}

export function isEligibleForMetabolicForWomenCrossSellMed() {
  return (
    isMetabolicMedCrossSellBaseEligible() &&
    isEligibleForMetabolicWomenSub() &&
    isPregnantStatusHealthCheckEligible() &&
    isNotPlanningToBePregnant()
  );
}

export function isEligibleForMetabolicForWomenCrossSellWeight() {
  return (
    isUserAuthenticated() &&
    isUS() &&
    isEnLanguage() &&
    isRouteEligibleForWeightCrossSell() &&
    isStateEligibleForMetabolicWeightCrossSell(ADDON_SKU.OTC_METABOLIC_WOMEN) &&
    isEligibleForMetabolicWomenSub() &&
    isPregnantStatusHealthCheckEligible()
  );
}

export function getPlandisplayBilling() {
  const { plandisplay, billing } = getURLParams();

  return {
    plandisplay: plandisplay ?? getMeristemConfigValue("plandisplay"),
    billing: billing ?? getMeristemConfigValue("billing"),
  };
}

export function isMicrodoseOneVsThreeEnabled() {
  const { plandisplay } = getPlandisplayBilling();
  const { group } = getURLParams();

  return plandisplay === "M4X9K2" || (!plandisplay && group === "shopping");
}

export function isMicrodoseMonthlyEnabled() {
  const { plandisplay } = getPlandisplayBilling();

  return isMicrodosePaidTraffic() && plandisplay === "K5RU9Z";
}

export function isMicrodoseNoTrialEnabled() {
  const { plandisplay } = getPlandisplayBilling();

  return isMicrodosePaidTraffic() && plandisplay === "B7R3Q1";
}

export function isMicrodoseNoTrialSelected(state = getStore().getState()) {
  const { saleItem } = state.checkoutOrders;

  return (
    saleItem?.legacyNoomPlanId ===
    mapPlanKeyToNoomPlanId[clinicalPlans.compoundedMicrodoseNoTrial]
  );
}

export function isMicrodoseMonthlySelected(state = getStore().getState()) {
  const { saleItem } = state.checkoutOrders;
  const oneMonthNoomPlanId =
    mapPlanKeyToNoomPlanId[clinicalPlans.compoundedMicrodoseOneMonth];

  return (
    isMicrodoseMonthlyEnabled() &&
    !!saleItem?.legacyNoomPlanId &&
    !!oneMonthNoomPlanId &&
    saleItem.legacyNoomPlanId === oneMonthNoomPlanId
  );
}

export function hasNativePurchasedSubscription(state = getStore().getState()) {
  const { activeSubscription } = state.billingData;
  if (!isRequestSuccess(activeSubscription)) {
    return false;
  }
  const source = activeSubscription.data?.source;
  return source === "ITUNES" || source === "GOOGLE_PLAY";
}

hasNativePurchasedSubscription.load = async () => {
  await getStore().dispatch(loadActiveSubscription());
};

export function hasActiveWeightPremiumProgram(state = getStore().getState()) {
  const { activeSubscriptionProductName } = state.billingData;
  return isWeightOrPremiumProductName(activeSubscriptionProductName);
}

export function hasActiveMedProgram(state = getStore().getState()) {
  const { activeSubscriptionProductName } = state.billingData;
  return isMedProductName(activeSubscriptionProductName);
}

export function hasActiveGlp1WeightLossProgram(state = getStore().getState()) {
  const { activeSubscriptionProductName } = state.billingData;
  return isGlp1WeightLossMedProductName(activeSubscriptionProductName);
}

export function selectedMedicationNeedsMedicationDosageSelection() {
  const { selectedMedicationProductName } = getSessionState("browser");
  return DOSAGE_REQUIRED_PRODUCT_NAMES.has(
    selectedMedicationProductName as MedProductName
  );
}

export function selectedMedicationNeedsMedicationPlanLengthSelection() {
  const { selectedMedicationProductName } = getSessionState("browser");
  return COMPOUNDED_MEDICATION_PRODUCT_NAMES.has(
    selectedMedicationProductName as MedProductName
  );
}

export function hasSelectedNonSellableMedication() {
  const { selectedMedicationProductName } = getSessionState("browser");
  return NON_SELLABLE_PRODUCT_NAMES.has(
    selectedMedicationProductName as MedProductName
  );
}
